import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import {
  Layout as AntLayout,
  Select,
  Button,
  Card,
  Row,
  Col,
  Space,
  Statistic,
  Progress,
  Modal,
  Input,
  Switch,
  Table,
  Empty,
  Tooltip,
  Tag,
  Divider,
  Alert,
  ConfigProvider,
  theme,
  Radio
} from 'antd';
import {
  UserOutlined,
  DownloadOutlined,
  CalendarOutlined,
  SaveOutlined,
  UndoOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  DeleteOutlined,
  BgColorsOutlined,
  ArrowRightOutlined
} from '@ant-design/icons';
import axios from 'axios';
import Layout from '../../layout/Layout';
import Header from '../../components/Header';
import fetchData from '../../functions/fetchData';
import { getHeaders } from '../../utility/getHeader';
import './Payroll.css';
import {
  getPayrollPeriod,
  getPeriodDates,
  checkForDuplicatePayroll,
  createPayrollBatchesForDepartment,
  calculateSalaryForPeriod,
  formatPeriodLabel,
  getPeriodBadgeColor
} from '../../helpers/payrollPeriodHelpers';

const Payroll = () => {
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [payrollData, setPayrollData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM format
  const [selectedPeriod, setSelectedPeriod] = useState('full-month'); // 'full-month', '1-15', '16-30'
  const [gridTheme, setGridTheme] = useState('ag-theme-quartz'); // Theme selector
  const [gridKey, setGridKey] = useState(0); // Force re-render key
  const [activeRowId, setActiveRowId] = useState(null);

  // Auto-save states
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSaveTime, setLastSaveTime] = useState(null);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const [recoveryData, setRecoveryData] = useState(null);
  const [recoveryToastShown, setRecoveryToastShown] = useState(false);

  // Employee tracking states
  const [employeesWithoutPayroll, setEmployeesWithoutPayroll] = useState([]);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [payrollStats, setPayrollStats] = useState({
    totalEmployees: 0,
    completedPayroll: 0,
    remainingPayroll: 0,
    completionPercentage: 0
  });
  const [isRecoveredFromDb, setIsRecoveredFromDb] = useState(false);

  // Salary View Modal state
  const [showSalaryModal, setShowSalaryModal] = useState(false);

  // Auto-save configuration
  const AUTO_SAVE_INTERVAL = 30000; // 30 seconds
  const STORAGE_KEY_PREFIX = 'payroll_autosave_';

  // Available AG Grid themes
  const availableThemes = [
    { value: 'ag-theme-quartz', label: 'Quartz (Modern)' },
    { value: 'ag-theme-alpine', label: 'Alpine (Clean)' },
    { value: 'ag-theme-balham', label: 'Balham (Professional)' },
    { value: 'ag-theme-material', label: 'Material (Google)' }
  ];
  
  // Centralized JWT error handler
  const handleJWTError = useCallback((error) => {
    const errorMsg = error.response?.data?.error || error.response?.data?.message || error.message || '';
    const errorName = error.response?.data?.name || error.name || '';
    const statusCode = error.response?.status;
    
    // Check for JWT expiration - be very flexible with detection
    const isTokenExpired = 
      statusCode === 401 || 
      errorMsg.toLowerCase().includes('jwt expired') || 
      errorMsg.toLowerCase().includes('token expired') ||
      errorMsg.toLowerCase().includes('tokenexpirederror') ||
      errorMsg.toLowerCase().includes('authorization token missing') ||
      errorMsg.toLowerCase().includes('invalid token') ||
      errorName === 'TokenExpiredError' ||
      errorName === 'JsonWebTokenError';
    
    if (isTokenExpired) {
      console.error('🔒 JWT Token expired - logging out user');
      toast.error('🔒 Your session has expired. Redirecting to login...', { autoClose: 2000 });
      
      // Clear all authentication data
      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
      localStorage.removeItem('user');
      sessionStorage.removeItem('user');
      
      // Immediate redirect to login page
      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);
      
      return true; // Indicates error was handled
    }
    
    return false; // Not a JWT error
  }, []);

  // ROUND NET SALARY to nearest 5 or 10 (for clean payouts)
  const roundNetSalary = (value) => {
    const lastDigit = Math.floor(value) % 10;
    const baseValue = Math.floor(value / 10) * 10;
    
    if (lastDigit <= 2) {
      // Round down to nearest 5 (e.g., 4821 -> 4820)
      return baseValue;
    } else if (lastDigit <= 7) {
      // Round to nearest 5 (e.g., 4823 -> 4825, 6573 -> 6575)
      return baseValue + 5;
    } else {
      // Round up to nearest 10 (e.g., 7398 -> 7400)
      return baseValue + 10;
    }
  };

  // AG Grid ref
  const gridRef = React.useRef();

  const handleCellClicked = useCallback((params) => {
    const clickedRowId = params?.data?.rowId || null;
    setActiveRowId(clickedRowId);
  }, []);

  const handleCellFocused = useCallback((params) => {
    if (!gridRef.current?.api || params?.rowIndex == null || params.rowIndex < 0) {
      return;
    }

    const focusedNode = gridRef.current.api.getDisplayedRowAtIndex(params.rowIndex);
    const focusedRowId = focusedNode?.data?.rowId || null;

    if (focusedRowId) {
      setActiveRowId(focusedRowId);
    }
  }, []);

  const getPayrollRowClass = useCallback((params) => {
    return params?.data?.rowId && params.data.rowId === activeRowId ? 'payroll-row-active' : '';
  }, [activeRowId]);

  useEffect(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.redrawRows();
    }
  }, [activeRowId]);

  useEffect(() => {
    setActiveRowId(null);
  }, [selectedDepartment, selectedMonth]);

  // Auto-save utility functions
  const getStorageKey = useCallback(() => {
    return `${STORAGE_KEY_PREFIX}${selectedDepartment}_${selectedMonth}`;
  }, [selectedDepartment, selectedMonth, STORAGE_KEY_PREFIX]);

  const saveToLocalStorage = useCallback((data, isAutoSave = false) => {
    try {
      const storageKey = getStorageKey();
      const saveData = {
        payrollData: data,
        timestamp: new Date().toISOString(),
        department: selectedDepartment,
        month: selectedMonth,
        isAutoSave: isAutoSave
      };
      
      localStorage.setItem(storageKey, JSON.stringify(saveData));
      setLastSaveTime(new Date());
      setHasUnsavedChanges(false);
      
      if (isAutoSave) {
        console.log('Auto-saved payroll data at:', new Date().toLocaleTimeString());
        toast.success('📁 Data auto-saved successfully!', { 
          position: "bottom-right", 
          autoClose: 2000,
          hideProgressBar: true 
        });
      } else {
        toast.success('💾 Payroll data saved successfully!');
      }
    } catch (error) {
      console.error('Error saving to localStorage:', error);
      toast.error('Failed to save data locally');
    }
  }, [getStorageKey, selectedDepartment, selectedMonth]);

  const loadFromLocalStorage = useCallback(() => {
    try {
      const storageKey = getStorageKey();
      const savedData = localStorage.getItem(storageKey);
      
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        return parsedData;
      }
      return null;
    } catch (error) {
      console.error('Error loading from localStorage:', error);
      return null;
    }
  }, [getStorageKey]);

  const clearLocalStorage = useCallback(() => {
    try {
      const storageKey = getStorageKey();
      localStorage.removeItem(storageKey);
      setRecoveryData(null);
      setHasUnsavedChanges(false);
      toast.info('Local storage cleared');
    } catch (error) {
      console.error('Error clearing localStorage:', error);
    }
  }, [getStorageKey]);

  // Custom cell renderer for employee names without avatars
  const EmployeeNameRenderer = React.useCallback((params) => {
    const employeeName = params.value || 'Unknown Employee';
    
    if (!employeeName || employeeName.trim() === '' || employeeName === 'Unknown Employee') {
      return (
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted fw-medium" style={{ fontSize: '11px' }}>Unknown Employee</span>
        </div>
      );
    }
    
    return (
      <div className="d-flex align-items-center" style={{ width: '100%' }}>
        <span className="fw-semibold text-dark" style={{ fontSize: '11px' }}>{employeeName}</span>
      </div>
    );
  }, []);

  // Custom cell renderer for currency values
  const CurrencyRenderer = React.useCallback((params) => {
    const value = parseFloat(params.value) || 0;
    const color = value > 0 ? '#28a745' : '#6c757d';
    return (
      <div className="d-flex align-items-center gap-1">
        <span style={{ color, fontWeight: '600', fontSize: '10px' }}>
          ฿{value.toLocaleString('th-TH')}
        </span>
      </div>
    );
  }, []);

  // Custom cell renderer for status indicators
  const StatusRenderer = React.useCallback((params) => {
    const value = parseInt(params.value) || 0;
    let badge = 'success';
    let text = 'Good';
    
    if (value > 5) {
      badge = 'danger';
      text = 'High';
    } else if (value > 2) {
      badge = 'warning';
      text = 'Medium';
    }
    
    return value > 0 ? (
      <span className={`badge bg-${badge} rounded-pill px-1 py-0`} style={{ fontSize: '9px' }}>
        {value} ({text})
      </span>
    ) : (
      <span className="badge bg-success rounded-pill px-1 py-0" style={{ fontSize: '9px' }}>
        0 (Perfect)
      </span>
    );
  }, []);

  // Helper function to get days in the selected month
  const getDaysInSelectedMonth = React.useCallback(() => {
    if (!selectedMonth) return 30; // Default fallback
    const [year, month] = selectedMonth.split('-').map(Number);
    return new Date(year, month, 0).getDate(); // Gets last day of the month
  }, [selectedMonth]);

  // Helper function to auto-fill missing data
  const autoFillMissingData = React.useCallback((employee) => {
    return {
      // Auto-fill missing fields with reasonable defaults
      title: employee.title || (employee.gender === 'Male' ? 'Mr.' : employee.gender === 'Female' ? 'Ms.' : ''),
      nationality: employee.nationality || 'Thai',
      employmentType: employee.employment_type || 'Full-time',
      status: employee.status || 'Active',
      phoneNumber: employee.phone_number || 'Not provided',
      email: employee.email || 'Not provided',
      hireDate: employee.hire_date || new Date().toISOString().split('T')[0],
      // Auto-generate employee ID if missing
      employeeId: employee.employee_id || `EMP${String(employee.id).padStart(3, '0')}`,
      // Auto-fill salary type based on amount
      salaryType: employee.salary_type || (employee.salary_amount > 50000 ? 'Monthly' : 'half-monthly')
    };
  }, []);

  const generatePayrollData = React.useCallback((employeeList) => {
    //console.log('Employee data structure:', employeeList[0]); // Debug log
    const daysInMonth = getDaysInSelectedMonth();
  //  console.log('Days in selected month:', daysInMonth); // Debug log
    
    // Clear existing data first
    setPayrollData([]);
    
    const payrollRecords = employeeList.map((employee, index) => {
      // Auto-fill missing data
      const autoFilledData = autoFillMissingData(employee);
      
      // Handle employee names with proper field mapping
      const thaiName = `${employee.thai_first_name || ''} ${employee.thai_last_name || ''}`.trim();
      const englishName = `${employee.english_first_name || ''} ${employee.english_last_name || ''}`.trim();
      const displayName = englishName || thaiName || employee.name || employee.employee_name || `Employee ${employee.id}`;
      
      // Handle salary with proper field mapping
      const salaryAmount = parseFloat(employee.salary_amount || employee.salary || employee.monthly_salary || 0);
      const salaryType = autoFilledData.salaryType;
      
      // Create a complete flat record for each employee with ALL fields populated
      const payrollRecord = {
        // Essential identifiers
        id: employee.id,
        rowId: `emp_${employee.id}`,
        
        // Employee Information Group - ALL fields must be present
        employeeName: displayName,
        employeeId: autoFilledData.employeeId,
        position: employee.position_name || employee.position || employee.job_title || 'N/A',
        
        // Salary Information Group - ALL fields must be present
        salaryType: salaryType,
        monthlySalary: salaryAmount,
        perDaySalary: salaryAmount > 0 ? parseFloat((salaryAmount / daysInMonth).toFixed(2)) : 0,
        
        // Attendance Tracking Group - ALL fields must be present
        numberOfDays: daysInMonth,
        workDays: salaryType === 'half-monthly' ? 15 : daysInMonth,
        absentDays: 0,
        lateDays: 0,
        
        // Deductions & Penalties Group - ALL fields must be present (amounts in THB)
        sso: 0,
        notCheckout: 0,
        notCheckin: 0,
        latePenalty: 0,
        
        // Deductions & Adjustments Group - ALL fields must be present
        pending: 0,
        cut: 0,
        advanceDeduction: 0,
        
        // Additional Income Group - ALL fields must be present
        bonus: 0,
        overtime: 0,
        otherIncome: 0,
        
        // Notes & Remarks Group - ALL fields must be present
        noteRemark: '',
        
        // Hidden fields for reference
        _originalData: employee,
        _thaiName: thaiName,
        _englishName: englishName,
        _autoFilled: autoFilledData
      };
      
    //  console.log(`Employee ${index + 1} record:`, payrollRecord);
      return payrollRecord;
    });
    
   // console.log('All generated payroll records:', payrollRecords);
    
    // Use setTimeout to ensure state update happens in next tick
    setTimeout(() => {
      setPayrollData(payrollRecords);
      setGridKey(prev => prev + 1);
    }, 100);
    
  }, [getDaysInSelectedMonth, autoFillMissingData]);

  const fetchDepartments = React.useCallback(async () => {
    try {
      const data = await fetchData('employee_departments', null, 'id', {});
      setDepartments(data || []);
    } catch (error) {
      toast.error('Failed to fetch departments');
      console.error('Error fetching departments:', error);
      if (handleJWTError(error)) return;
    }
  }, [handleJWTError]);

  const normalizeSalaryTypeForGrid = React.useCallback((value) => {
    if (!value) return 'Monthly';
    const normalized = String(value).toLowerCase();
    if (normalized === 'half-monthly' || normalized === 'half_monthly' || normalized === 'half monthly') {
      return 'half-monthly';
    }
    if (normalized === 'daily') return 'daily';
    return 'Monthly';
  }, []);

  const fetchExistingBatchesForSelection = React.useCallback(async () => {
    try {
      if (!selectedDepartment || !selectedMonth) return [];

      const [year, month] = selectedMonth.split('-');
      const monthNumber = String(parseInt(month, 10));
      const departmentId = parseInt(selectedDepartment, 10);

      const response = await axios.get(
        `/hr/batches?year=${year}&limit=1000`,
        getHeaders()
      );

      if (response.status !== 200) return [];

      const result = response.data;
      if (result.success && result.data && result.data.batches && result.data.batches.length > 0) {
        const matchingBatches = result.data.batches.filter((batch) => {
          const batchDepartmentId = parseInt(batch.department_id, 10);
          const rawMonth = String(batch.payroll_month || '').trim();
          const normalizedBatchMonth = rawMonth.includes('-')
            ? String(parseInt(rawMonth.split('-')[1], 10))
            : String(parseInt(rawMonth, 10));

          return batchDepartmentId === departmentId && normalizedBatchMonth === monthNumber;
        });

        return matchingBatches.sort((a, b) => {
          const aTime = new Date(a.created_at || 0).getTime();
          const bTime = new Date(b.created_at || 0).getTime();
          return bTime - aTime;
        });
      }

      return [];
    } catch (error) {
      console.error('Error checking existing batch:', error);
      if (handleJWTError(error)) return [];
      return [];
    }
  }, [selectedDepartment, selectedMonth, handleJWTError]);

  const loadPayrollRecordsFromBatch = React.useCallback(async (batchId) => {
    try {
      const recordsResponse = await axios.get(`/hr/records/${batchId}`, getHeaders());
      if (recordsResponse.status !== 200) {
        throw new Error('Failed to fetch payroll records');
      }

      const recordsResult = recordsResponse.data;
      if (!recordsResult.success || !recordsResult.data) {
        throw new Error('No payroll records found');
      }

      const records = recordsResult.data;
      const mappedRecords = records.map((record) => ({
        id: record.employee_id,
        rowId: `emp_${record.employee_id}`,
        employeeName: record.employee_name || `Employee ${record.employee_id}`,
        employeeId: record.employee_number || `EMP${String(record.employee_id).padStart(3, '0')}`,
        position: record.position || 'N/A',
        salaryType: normalizeSalaryTypeForGrid(record.salary_type),
        monthlySalary: parseFloat(record.monthly_salary) || 0,
        perDaySalary: parseFloat(record.per_day_salary) || 0,
        numberOfDays: parseInt(record.total_days_in_month) || getDaysInSelectedMonth(),
        workDays: parseInt(record.work_days) || getDaysInSelectedMonth(),
        absentDays: parseInt(record.absent_days) || 0,
        lateDays: parseFloat(record.late_days) || 0,
        sso: parseFloat(record.sso_deduction) || 0,
        notCheckout: parseFloat(record.not_checkout_penalty) || 0,
        notCheckin: parseFloat(record.not_checkin_penalty) || 0,
        latePenalty: parseFloat(record.late_penalty) || 0,
        pending: parseFloat(record.pending_deduction) || 0,
        cut: parseFloat(record.cut_deduction) || 0,
        advanceDeduction: parseFloat(record.advance_deduction) || 0,
        bonus: parseFloat(record.bonus) || 0,
        overtime: parseFloat(record.overtime) || 0,
        otherIncome: parseFloat(record.other_income) || 0,
        noteRemark: record.note_remark || '',
        _originalData: record
      }));

      setPayrollData([]);
      setTimeout(() => {
        setPayrollData(mappedRecords);
        setGridKey(prev => prev + 1);
      }, 100);

      return mappedRecords.length;
    } catch (error) {
      console.error('Error loading payroll records from batch:', error);
      if (handleJWTError(error)) return 0;
      throw error;
    }
  }, [getDaysInSelectedMonth, normalizeSalaryTypeForGrid, handleJWTError]);

  const promptRecoverFromDatabase = React.useCallback((batches, departmentName, monthLabel) => {
    return new Promise((resolve) => {
      if (!batches || batches.length === 0) {
        resolve(null);
        return;
      }

      let selectedBatchId = batches[0]?.id;
      const hasMultipleBatches = batches.length > 1;

      Modal.confirm({
        title: hasMultipleBatches ? 'Multiple payroll batches found' : 'Payroll records found',
        content: (
          <div>
            <p>Existing payroll data was found for:</p>
            <p><strong>{departmentName}</strong> - <strong>{monthLabel}</strong></p>
            {hasMultipleBatches ? (
              <>
                <p>Please choose which batch to recover:</p>
                <Radio.Group
                  defaultValue={selectedBatchId}
                  onChange={(e) => {
                    selectedBatchId = e.target.value;
                  }}
                  style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
                >
                  {batches.map((batch) => (
                    <Radio key={batch.id} value={batch.id}>
                      <span style={{ fontWeight: 600 }}>{batch.batch_number || `Batch ${batch.id}`}</span>
                      {batch.payroll_period ? <span style={{ marginLeft: 8, color: '#666' }}>({batch.payroll_period})</span> : null}
                    </Radio>
                  ))}
                </Radio.Group>
              </>
            ) : (
              <p>Batch: <strong>{batches[0].batch_number || `Batch ${batches[0].id}`}</strong></p>
            )}
            <p>Do you want to recover the saved data from the database?</p>
          </div>
        ),
        okText: 'Recover from Database',
        cancelText: 'Start New Payroll',
        width: hasMultipleBatches ? 560 : 480,
        onOk: () => {
          const selectedBatch = batches.find((batch) => String(batch.id) === String(selectedBatchId));
          resolve(selectedBatch || null);
        },
        onCancel: () => resolve(null)
      });
    });
  }, []);

  const fetchEmployeesByDepartment = React.useCallback(async () => {
    try {
      setLoading(true);
      setPayrollData([]); // Clear existing data first
      setIsRecoveredFromDb(false);
      
      // Fetch employees with position names using JOIN
      const query = `
        SELECT e.*, p.position_name 
        FROM employees e 
        LEFT JOIN employee_positions p ON e.position_id = p.id 
        WHERE e.department_id = ?
      `;
      
      const data = await fetchData('employees', query, 'id', {
        department_id: selectedDepartment
      });
      
      // Handle empty or null data
      const employeeData = data || [];
   //   console.log('Fetched employee data with positions:', employeeData); // Debug log
      setEmployees(employeeData);
      
      if (employeeData.length === 0) {
        setPayrollData([]);
        toast.info('No employees found in this department');
      } else {
        const existingBatches = await fetchExistingBatchesForSelection();

        if (existingBatches.length > 0) {
          const departmentName = departments.find(d => d.id === selectedDepartment)?.department_name || 'Selected Department';
          const monthLabel = new Date(selectedMonth + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
          const selectedBatch = await promptRecoverFromDatabase(existingBatches, departmentName, monthLabel);

          if (selectedBatch) {
            const recoveredCount = await loadPayrollRecordsFromBatch(selectedBatch.id);
            setIsRecoveredFromDb(true);
            setHasUnsavedChanges(false);
            toast.success(`Recovered ${recoveredCount} payroll records from ${selectedBatch.batch_number || `Batch ${selectedBatch.id}`}`);
            return;
          }
        }

        // Add delay to ensure proper data handling
        setTimeout(() => {
          generatePayrollData(employeeData);
          toast.success(`Loaded ${employeeData.length} employees with position details`);
        }, 300);
      }
    } catch (error) {
      toast.error('Failed to fetch employees');
      console.error('Error fetching employees:', error);
      if (handleJWTError(error)) {
        setEmployees([]);
        setPayrollData([]);
        return;
      }
      setEmployees([]);
      setPayrollData([]);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 500);
    }
  }, [
    selectedDepartment,
    selectedMonth,
    departments,
    generatePayrollData,
    fetchExistingBatchesForSelection,
    promptRecoverFromDatabase,
    loadPayrollRecordsFromBatch
  ]);

  // Fetch all employees across all departments for payroll tracking
  const fetchAllEmployees = React.useCallback(async () => {
    try {
      // Fetch all three datasets separately since fetchData doesn't support JOINs
      const [employeesData, departmentsData, positionsData] = await Promise.all([
        fetchData('employees', null, 'id', {}),
        fetchData('employee_departments', null, 'id', {}),
        fetchData('employee_positions', null, 'id', {})
      ]);
      
      // Create lookup maps for efficient joining
      const departmentMap = {};
      const positionMap = {};
      
      if (departmentsData) {
        departmentsData.forEach(dept => {
          departmentMap[dept.id] = dept.department_name;
        });
      }
      
      if (positionsData) {
        positionsData.forEach(pos => {
          positionMap[pos.id] = pos.position_name;
        });
      }
      
      // Join the data in JavaScript
      const enrichedEmployees = (employeesData || []).map(employee => ({
        ...employee,
        department_name: employee.department_id ? departmentMap[employee.department_id] : null,
        position_name: employee.position_id ? positionMap[employee.position_id] : null
      }));
      
      // Sort by department and name
      enrichedEmployees.sort((a, b) => {
        const deptCompare = (a.department_name || '').localeCompare(b.department_name || '');
        if (deptCompare !== 0) return deptCompare;
        
        const nameA = a.english_first_name || a.thai_first_name || '';
        const nameB = b.english_first_name || b.thai_first_name || '';
        return nameA.localeCompare(nameB);
      });
      
      return enrichedEmployees;
    } catch (error) {
      console.error('Error fetching all employees:', error);
      if (handleJWTError(error)) return [];
      return [];
    }
  }, [handleJWTError]);

  // Fetch existing payroll records for current month
  const fetchExistingPayrollRecords = React.useCallback(async () => {
    try {
      if (!selectedMonth) return [];
      
      const [year, month] = selectedMonth.split('-');
      const monthNumber = String(parseInt(month, 10)); // Convert '02' to '2' to match database
      
      // Use simple version of batches endpoint
      const response = await axios.get(`/hr/batches?year=${year}&limit=1000`, getHeaders());
      
      if (response.status !== 200) {
        throw new Error('Failed to fetch payroll batches');
      }
      
      const result = response.data;
      
      if (result.success && result.data && result.data.batches) {
        // Filter batches for selected month and get all employee IDs with payroll
        const monthBatches = result.data.batches.filter(batch => 
          batch.payroll_month === monthNumber
        );
        
      //  console.log(`Found ${monthBatches.length} batches for month ${monthNumber}`);
        
        const employeeIdsWithPayroll = [];
        
        // Fetch records for each batch
        for (const batch of monthBatches) {
          try {
            const recordsResponse = await axios.get(`/hr/records/${batch.id}`, getHeaders());
            if (recordsResponse.status === 200) {
              const recordsResult = recordsResponse.data;
              if (recordsResult.success && recordsResult.data) {
                recordsResult.data.forEach(record => {
                  if (!employeeIdsWithPayroll.includes(record.employee_id)) {
                    employeeIdsWithPayroll.push(record.employee_id);
                  }
                });
              }
            }
          } catch (error) {
            console.error(`Error fetching records for batch ${batch.id}:`, error);
          }
        }
        
       // console.log(`Total employees with payroll: ${employeeIdsWithPayroll.length}`);
        return employeeIdsWithPayroll;
      }
      
      return [];
    } catch (error) {
      console.error('Error fetching existing payroll records:', error);
      if (handleJWTError(error)) return [];
      return [];
    }
  }, [selectedMonth, handleJWTError]);

  // Calculate payroll statistics
  const calculatePayrollStats = React.useCallback(async () => {
    try {
      const allEmployeesData = await fetchAllEmployees();
      const employeesWithPayrollIds = await fetchExistingPayrollRecords();
      
      const employeesWithPayroll = allEmployeesData.filter(emp => 
        employeesWithPayrollIds.includes(emp.id)
      );
      
      const employeesWithoutPayroll = allEmployeesData.filter(emp => 
        !employeesWithPayrollIds.includes(emp.id)
      );
      
      const totalEmployees = allEmployeesData.length;
      const completedPayroll = employeesWithPayroll.length;
      const remainingPayroll = employeesWithoutPayroll.length;
      const completionPercentage = totalEmployees > 0 ? 
        Math.round((completedPayroll / totalEmployees) * 100) : 0;
      
      setEmployeesWithoutPayroll(employeesWithoutPayroll);
      setPayrollStats({
        totalEmployees,
        completedPayroll,
        remainingPayroll,
        completionPercentage
      });
      
    } catch (error) {
      console.error('Error calculating payroll stats:', error);
      if (handleJWTError(error)) return;
    }
  }, [fetchAllEmployees, fetchExistingPayrollRecords, handleJWTError]);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  useEffect(() => {
    if (selectedDepartment) {
      fetchEmployeesByDepartment();
    }
  }, [selectedDepartment, fetchEmployeesByDepartment]);

  // Calculate payroll stats when month changes
  useEffect(() => {
    if (selectedMonth) {
      calculatePayrollStats();
    }
  }, [selectedMonth, calculatePayrollStats]);

  // Regenerate payroll data when month changes
  useEffect(() => {
    if (employees.length > 0 && !isRecoveredFromDb) {
      generatePayrollData(employees);
    }
  }, [selectedMonth, employees, generatePayrollData, isRecoveredFromDb]);

  // Debug effect to log payroll data structure
  useEffect(() => {
    if (payrollData.length > 0) {
      console.log('=== PAYROLL DATA VALIDATION ===');
      console.log('Total records:', payrollData.length);
      console.log('First employee record structure:', Object.keys(payrollData[0]));
      
      // Validate each record has all required fields
      const requiredFields = [
        'id', 'rowId', 'employeeName', 'employeeId', 'position',
        'salaryType', 'monthlySalary', 'perDaySalary',
        'numberOfDays', 'workDays', 'absentDays', 'lateDays',
        'sso', 'notCheckout', 'notCheckin', 'latePenalty',
        'pending', 'cut', 'advanceDeduction', 'noteRemark'
      ];
      
      payrollData.forEach((record, index) => {
        const missingFields = requiredFields.filter(field => !(field in record));
        if (missingFields.length > 0) {
          console.error(`Record ${index} (${record.employeeName}) missing fields:`, missingFields);
        }
        
        // Log sample data for first 3 records
        if (index < 3) {
          console.log(`Record ${index} (${record.employeeName}):`, {
            id: record.id,
            rowId: record.rowId,
            employeeName: record.employeeName,
            position: record.position,
            monthlySalary: record.monthlySalary,
            numberOfDays: record.numberOfDays
          });
        }
      });
      
   //   console.log('=== END VALIDATION ===');
    }
  }, [payrollData]);

  // Real-time summary calculations - matches backend calculation logic
  const payrollSummary = useMemo(() => {
    if (!payrollData || payrollData.length === 0) {
      return {
        totalEmployees: 0,
        totalGrossSalary: 0,
        totalDeductions: 0,
        totalNetSalary: 0,
        totalBonus: 0,
        totalOvertime: 0,
        totalOtherIncome: 0,
        totalAbsentDays: 0
      };
    }

    let totalGrossSalary = 0;
    let totalDeductions = 0;
    let totalNetSalary = 0;
    let totalBonus = 0;
    let totalOvertime = 0;
    let totalOtherIncome = 0;
    let totalAbsentDays = 0;

    payrollData.forEach((record) => {
      // Calculate base salary (matches backend calculateGrossSalary logic)
      let baseSalary = 0;
      const salaryType = record.salaryType || 'Monthly';
      const monthlySalary = parseFloat(record.monthlySalary) || 0;
      const perDaySalary = parseFloat(record.perDaySalary) || 0;
      const workDays = parseInt(record.workDays) || 0;

      if (salaryType === 'daily') {
        baseSalary = perDaySalary * workDays;
      } else if (salaryType === 'half-monthly') {
        // CRITICAL: matches backend fix - exactly HALF of monthly salary
        baseSalary = monthlySalary / 2;
      } else {
        // Monthly (default)
        baseSalary = monthlySalary;
      }

      // Parse additions
      const bonus = parseFloat(record.bonus) || 0;
      const overtime = parseFloat(record.overtime) || 0;
      const otherIncome = parseFloat(record.otherIncome) || 0;

      // Calculate gross salary (baseSalary + all additions)
      const grossSalary = baseSalary + bonus + overtime + otherIncome;

      // Calculate deductions (matches backend calculateTotalDeductions logic)
      const sso = parseFloat(record.sso) || 0;
      const notCheckout = parseFloat(record.notCheckout) || 0;
      const notCheckin = parseFloat(record.notCheckin) || 0;
      const latePenalty = parseFloat(record.latePenalty) || 0;
      const pending = parseFloat(record.pending) || 0;
      const cut = parseFloat(record.cut) || 0;
      const advanceDeduction = parseFloat(record.advanceDeduction) || 0;
      const absentDays = parseInt(record.absentDays) || 0;
      const absentDeduction = absentDays * perDaySalary;

      const totalRecordDeductions = sso + notCheckout + notCheckin + latePenalty + pending + cut + advanceDeduction + absentDeduction;

      // Calculate net salary (matches backend calculateNetSalary logic)
      const rawNetSalary = grossSalary - totalRecordDeductions;
      const netSalary = roundNetSalary(rawNetSalary);

      // Accumulate totals
      totalGrossSalary += grossSalary;
      totalDeductions += totalRecordDeductions;
      totalNetSalary += netSalary;
      totalBonus += bonus;
      totalOvertime += overtime;
      totalOtherIncome += otherIncome;
      totalAbsentDays += absentDays;
    });

    return {
      totalEmployees: payrollData.length,
      totalGrossSalary,
      totalDeductions,
      totalNetSalary,
      totalBonus,
      totalOvertime,
      totalOtherIncome,
      totalAbsentDays
    };
  }, [payrollData]);

  // Auto-save effect - check for recovery data on component mount
  useEffect(() => {
    // Only check for recovery data once when component mounts
    if (!recoveryToastShown) {
      const savedData = loadFromLocalStorage();
      if (savedData && savedData.payrollData && savedData.payrollData.length > 0) {
        setRecoveryData(savedData);
        setRecoveryToastShown(true); // Mark toast as shown
        
        toast.warn(
          <div>
            <ExclamationCircleOutlined className="me-2" />
            <strong>Recovery Data Found!</strong><br />
            Found unsaved changes from {new Date(savedData.timestamp).toLocaleString()}
          </div>,
          {
            position: "bottom-right",
            autoClose: 8000, // Auto close after 8 seconds instead of never
            hideProgressBar: false,
            closeOnClick: true, // Allow closing on click
            draggable: false,
            toastId: 'recovery-data-toast' // Unique ID to prevent duplicates
          }
        );
      }
    }
  }, [recoveryToastShown, loadFromLocalStorage]); // Include loadFromLocalStorage dependency

  // Auto-save effect - save data periodically when changes are detected
  useEffect(() => {
    if (!autoSaveEnabled || payrollData.length === 0) return;

    const autoSaveTimer = setInterval(() => {
      if (hasUnsavedChanges) {
        saveToLocalStorage(payrollData, true);
      }
    }, AUTO_SAVE_INTERVAL);

    return () => clearInterval(autoSaveTimer);
  }, [payrollData, hasUnsavedChanges, autoSaveEnabled, saveToLocalStorage, AUTO_SAVE_INTERVAL]);

  // Auto-save effect - detect changes in payroll data
  useEffect(() => {
    if (payrollData.length > 0) {
      setHasUnsavedChanges(true);
    }
  }, [payrollData]);

  // Browser beforeunload warning
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Column definitions for AG Grid with improved organization
  const columnDefs = useMemo(() => [
    // Employee Information Group
    {
      headerName: '👤 Employee Information',
      headerClass: 'ag-header-group-custom',
      openByDefault: true,
      children: [
        {
          field: 'employeeName',
          headerName: 'Employee Name',
          width: 200,
          pinned: 'left',
          cellRenderer: EmployeeNameRenderer,
          headerClass: 'ag-header-custom',
          cellClass: 'ag-cell-focus',
          cellStyle: {
            padding: '4px 6px',
            display: 'flex',
            alignItems: 'center',
            backgroundColor: '#fafbfc',
            borderRight: '1px solid #e2e8f0',
            fontSize: '10px'
          },
          suppressMovable: true
        },
        {
          field: 'employeeId',
          headerName: 'Employee ID',
          width: 100,
          pinned: 'left',
          headerClass: 'ag-header-custom',
          cellStyle: { 
            fontFamily: 'monospace', 
            fontWeight: 'bold',
            backgroundColor: '#f8f9fa',
            color: '#495057',
            textAlign: 'center',
            padding: '4px 6px',
            borderRight: '1px solid #e2e8f0',
            fontSize: '9px'
          },
          suppressMovable: true
        },
        {
          field: 'position',
          headerName: 'Position',
          width: 140,
          pinned: 'left',
          headerClass: 'ag-header-custom',
          cellStyle: {
            backgroundColor: '#e3f2fd',
            color: '#1976d2',
            fontWeight: '500',
            padding: '4px 6px',
            borderRight: '1px solid #e2e8f0',
            fontSize: '10px'
          },
          suppressMovable: true
        }
      ]
    },
    // Salary Information Group
    {
      headerName: '💰 Salary Information',
      headerClass: 'ag-header-group-custom',
      openByDefault: true,
      children: [
        {
          field: 'salaryType',
          headerName: 'Salary Type',
          width: 110,
          editable: true,
          cellEditor: 'agSelectCellEditor',
          cellEditorParams: {
            values: ['Monthly', 'half-monthly']
          },
          headerClass: 'ag-header-custom',
          cellClass: 'ag-cell-editable',
          cellRenderer: (params) => {
            const type = params.value || 'Monthly';
            const badgeColor = type === 'Monthly' ? 'primary' : type === 'half-monthly' ? 'success' : 'secondary';
            return React.createElement('span', {
              className: `badge bg-${badgeColor} rounded-pill px-1 py-0`,
              style: { fontSize: '9px' }
            }, type);
          },
          cellStyle: { 
            backgroundColor: '#f8f9fa',
            textAlign: 'center',
            padding: '4px 6px',
            fontSize: '10px'
          }
        },
        {
          field: 'monthlySalary',
          headerName: 'Monthly Salary', // Shows full monthly amount
          width: 120,
          editable: true,
          cellEditor: 'agNumberCellEditor',
          cellRenderer: CurrencyRenderer,
          headerClass: 'ag-header-custom',
          cellClass: 'ag-cell-editable',
          cellStyle: { backgroundColor: '#f0f8ff', fontSize: '10px' }
        },
        {
          field: 'perDaySalary',
          headerName: 'Per Day Rate', // Calculated from monthly salary ÷ work days
          width: 110,
          cellRenderer: CurrencyRenderer,
          headerClass: 'ag-header-custom',
          valueGetter: (params) => {
            // Source: Full monthly salary amount
            const monthlySalary = params.data.monthlySalary || 0;
            
            // Calculation: Monthly salary ÷ total days in month (actual calendar days)
            const calculationDays = getDaysInSelectedMonth();
            return (monthlySalary / calculationDays).toFixed(2);
          },
          cellStyle: { backgroundColor: '#f5f5f5', fontStyle: 'italic', fontSize: '10px' }
        },
        {
          field: 'payableSalary',
          headerName: 'Payable Salary', // Actual salary amount for the period
          width: 130,
          cellRenderer: CurrencyRenderer,
          headerClass: 'ag-header-custom',
          valueGetter: (params) => {
            // Source: Full monthly salary amount
            const monthlySalary = params.data.monthlySalary || 0;
            const salaryType = params.data.salaryType || 'Monthly';
            
            // Calculate actual payable amount based on salary type
            if (salaryType === 'half-monthly') {
              // Half-monthly: Pay half of monthly salary (e.g., ₿15,000 → ₿7,500)
              return (monthlySalary / 2).toFixed(2);
            } else {
              // Monthly: Pay full monthly salary (e.g., ₿15,000 → ₿15,000)
              return monthlySalary.toFixed(2);
            }
          },
          cellStyle: { 
            backgroundColor: '#e8f5e8', 
            fontWeight: 'bold', 
            fontSize: '11px',
            color: '#2e7d32'
          }
        }
      ]
    },
    // Attendance Tracking Group
    {
      headerName: '📅 Attendance Tracking',
      headerClass: 'ag-header-group-custom',
      openByDefault: true,
      children: [
        {
          field: 'numberOfDays',
          headerName: 'Total Days',
          width: 90,
          editable: true,
          cellEditor: 'agNumberCellEditor',
          cellEditorParams: { min: 1, max: getDaysInSelectedMonth() },
          headerClass: 'ag-header-custom',
          cellClass: 'ag-cell-editable',
          cellStyle: { backgroundColor: '#fff9c4', textAlign: 'center', fontSize: '10px' }
        },
        {
          field: 'workDays',
          headerName: 'Work Days',
          width: 100,
          editable: true,
          cellEditor: 'agSelectCellEditor',
          cellEditorParams: { values: [15, getDaysInSelectedMonth()] },
          headerClass: 'ag-header-custom',
          cellClass: 'ag-cell-editable',
          cellRenderer: (params) => {
            const value = params.value;
            const salaryType = params.data.salaryType;
            const isHalfMonthly = salaryType === 'half-monthly';
            
            return React.createElement('div', {
              style: {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                fontSize: '11px',
                fontWeight: 'bold',
                color: isHalfMonthly ? '#1976d2' : '#2e7d32'
              }
            }, [
              React.createElement('span', { key: 'value' }, value),
              
            ]);
          },
          cellStyle: (params) => {
            const isHalfMonthly = params.data.salaryType === 'half-monthly';
            return {
              backgroundColor: isHalfMonthly ? '#e3f2fd' : '#e8f5e8',
              textAlign: 'center',
              fontWeight: 'bold',
              fontSize: '10px'
            };
          }
        },
        {
          field: 'absentDays',
          headerName: 'Absent Days',
          width: 100,
          editable: true,
          cellEditor: 'agNumberCellEditor',
          cellRenderer: StatusRenderer,
          headerClass: 'ag-header-custom',
          cellClass: 'ag-cell-editable'
        },
        {
          field: 'lateDays',
          headerName: 'Late Amount', // Changed from 'Late Days' to amount
          width: 100,
          editable: true,
          cellEditor: 'agNumberCellEditor',
          cellRenderer: CurrencyRenderer, // Changed from StatusRenderer to CurrencyRenderer
          headerClass: 'ag-header-custom',
          cellClass: 'ag-cell-editable',
          cellStyle: { backgroundColor: '#fff3e0', textAlign: 'center', fontSize: '10px' }
        }
      ]
    },
    // Check-in/out Issues Group
    {
      headerName: '⚠️ Deductions & Penalties',
      headerClass: 'ag-header-group-custom',
      openByDefault: true,
      children: [
        {
          field: 'sso',
          headerName: 'SSO Deduction',
          width: 110,
          editable: true,
          cellEditor: 'agNumberCellEditor',
          cellRenderer: CurrencyRenderer,
          headerClass: 'ag-header-custom',
          cellClass: 'ag-cell-editable',
          cellStyle: { backgroundColor: '#fff3e0', textAlign: 'center', fontSize: '10px' }
        },
        {
          field: 'notCheckout',
          headerName: 'Checkout Penalty',
          width: 130,
          editable: true,
          cellEditor: 'agNumberCellEditor',
          cellRenderer: CurrencyRenderer,
          headerClass: 'ag-header-custom',
          cellClass: 'ag-cell-editable',
          cellStyle: { backgroundColor: '#ffebee', textAlign: 'center', fontSize: '10px' }
        },
        {
          field: 'notCheckin',
          headerName: 'Check-in Penalty',
          width: 130,
          editable: true,
          cellEditor: 'agNumberCellEditor',
          cellRenderer: CurrencyRenderer,
          headerClass: 'ag-header-custom',
          cellClass: 'ag-cell-editable',
          cellStyle: { backgroundColor: '#ffebee', textAlign: 'center', fontSize: '10px' }
        },
        {
          field: 'latePenalty',
          headerName: 'Late Penalty',
          width: 110,
          editable: true,
          cellEditor: 'agNumberCellEditor',
          cellRenderer: CurrencyRenderer,
          headerClass: 'ag-header-custom',
          cellClass: 'ag-cell-editable',
          cellStyle: { backgroundColor: '#fff3e0', textAlign: 'center', fontSize: '10px' }
        }
      ]
    },
    // Deductions & Adjustments Group
    {
      headerName: '💸 Deductions & Adjustments',
      headerClass: 'ag-header-group-custom',
      openByDefault: true,
      children: [
        {
          field: 'pending',
          headerName: 'Pending Amount',
          width: 120,
          editable: true,
          cellEditor: 'agNumberCellEditor',
          cellRenderer: CurrencyRenderer,
          headerClass: 'ag-header-custom',
          cellClass: 'ag-cell-editable',
          cellStyle: { backgroundColor: '#ffeaa7', fontSize: '10px' }
        },
        {
          field: 'cut',
          headerName: 'Salary Cut',
          width: 100,
          editable: true,
          cellEditor: 'agNumberCellEditor',
          cellRenderer: CurrencyRenderer,
          headerClass: 'ag-header-custom',
          cellClass: 'ag-cell-editable',
          cellStyle: { backgroundColor: '#ffebee', fontSize: '10px' }
        },
        {
          field: 'advanceDeduction',
          headerName: 'Advance Deduction',
          width: 140,
          editable: true,
          cellEditor: 'agNumberCellEditor',
          cellRenderer: CurrencyRenderer,
          headerClass: 'ag-header-custom',
          cellClass: 'ag-cell-editable',
          cellStyle: { backgroundColor: '#fce4ec', fontSize: '10px' }
        }
      ]
    },
    // Additional Income Group
    {
      headerName: '💰 Additional Income',
      headerClass: 'ag-header-group-custom',
      openByDefault: true,
      children: [
        {
          field: 'bonus',
          headerName: 'Bonus',
          width: 100,
          editable: true,
          cellEditor: 'agNumberCellEditor',
          cellRenderer: CurrencyRenderer,
          headerClass: 'ag-header-custom',
          cellClass: 'ag-cell-editable',
          cellStyle: { backgroundColor: '#e8f5e8', fontSize: '10px' }
        },
        {
          field: 'overtime',
          headerName: 'Overtime Pay',
          width: 120,
          editable: true,
          cellEditor: 'agNumberCellEditor',
          cellRenderer: CurrencyRenderer,
          headerClass: 'ag-header-custom',
          cellClass: 'ag-cell-editable',
          cellStyle: { backgroundColor: '#e3f2fd', fontSize: '10px' }
        },
        {
          field: 'otherIncome',
          headerName: 'Other Income',
          width: 120,
          editable: true,
          cellEditor: 'agNumberCellEditor',
          cellRenderer: CurrencyRenderer,
          headerClass: 'ag-header-custom',
          cellClass: 'ag-cell-editable',
          cellStyle: { backgroundColor: '#f3e5f5', fontSize: '10px' }
        }
      ]
    },
    // Notes & Remarks Group
    {
      headerName: '📝 Notes & Remarks',
      headerClass: 'ag-header-group-custom',
      openByDefault: true,
      children: [
        {
          field: 'noteRemark',
          headerName: 'Notes/Remarks',
          width: 160,
          editable: true,
          cellEditor: 'agLargeTextCellEditor',
          cellEditorParams: { maxLength: 500 },
          headerClass: 'ag-header-custom',
          cellClass: 'ag-cell-editable',
          cellStyle: { backgroundColor: '#f3e5f5', padding: '4px', fontSize: '10px' },
          cellRenderer: (params) => {
            const value = params.value || '';
            if (value.length > 50) {
              return React.createElement('div', {
                title: value,
                style: { fontSize: '9px' }
              }, `${value.substring(0, 50)}...`);
            }
            return React.createElement('div', {
              style: { fontSize: '9px' }
            }, value);
          }
        }
      ]
    },
    {
      headerName: '🧮 Calculated Values',
      headerClass: 'ag-header-group-custom ag-header-calculated',
      children: [
        {
          field: 'totalDeductions',
          headerName: 'Total Deductions',
          width: 160,
          cellRenderer: CurrencyRenderer,
          headerClass: 'ag-header-custom ag-header-calculated',
          valueGetter: (params) => {
            // DISPLAY: Full monthly salary amount (e.g., ₿30,000)
            const monthlySalary = params.data.monthlySalary || 0;
            const salaryType = params.data.salaryType || 'Monthly';
            
            // CALCULATION: Use total days in month for per-day rate calculation  
            // All salary types: Actual calendar days in selected month
            const calculationDays = getDaysInSelectedMonth();
            const perDaySalary = monthlySalary / calculationDays;
            
            console.log(`Employee ${params.data.employeeName}: ${salaryType} - Using ${calculationDays} days for calculation`);
            
            // Calculate deductions from absent days using actual month days per-day rate
            const absentDeduction = (params.data.absentDays || 0) * perDaySalary;
            
            // Add all monetary penalties and deductions
            const totalDed = absentDeduction +
                           (params.data.sso || 0) +
                           (params.data.notCheckout || 0) +
                           (params.data.notCheckin || 0) +
                           (params.data.latePenalty || 0) +
                           (params.data.lateDays || 0) + // Late amount (not days)
                           (params.data.cut || 0) +
                           (params.data.advanceDeduction || 0) +
                           (params.data.pending || 0);
            
            return totalDed.toFixed(2);
          },
          cellStyle: { 
            backgroundColor: '#ffebee', 
            fontWeight: 'bold',
            border: '1px solid #f44336',
            color: '#d32f2f',
            padding: '8px 12px'
          }
        },
        {
          field: 'netSalary',
          headerName: 'Net Salary',
          width: 150,
          cellRenderer: CurrencyRenderer,
          headerClass: 'ag-header-custom ag-header-calculated',
          valueGetter: (params) => {
            // Get the actual payable salary for this period
            const monthlySalary = params.data.monthlySalary || 0;
            const salaryType = params.data.salaryType || 'Monthly';
            
            // Calculate actual payable amount based on salary type
            const payableSalary = salaryType === 'half-monthly' ? (monthlySalary / 2) : monthlySalary;
            
            // CALCULATION: Use total days in month for per-day rate calculation  
            // All salary types: Actual calendar days in selected month
            const calculationDays = getDaysInSelectedMonth();
            const perDaySalary = monthlySalary / calculationDays;
            
            // Calculate deductions from absent days using actual month days per-day rate
            const absentDeduction = (params.data.absentDays || 0) * perDaySalary;
            
            // Add all monetary penalties and deductions
            const totalDed = absentDeduction +
                           (params.data.sso || 0) +
                           (params.data.notCheckout || 0) +
                           (params.data.notCheckin || 0) +
                           (params.data.latePenalty || 0) +
                           (params.data.lateDays || 0) + // Late amount (not days)
                           (params.data.cut || 0) +
                           (params.data.advanceDeduction || 0) +
                           (params.data.pending || 0);
            
            // Add all additional income
            const totalAdditions = (params.data.bonus || 0) +
                                 (params.data.overtime || 0) +
                                 (params.data.otherIncome || 0);
            
            // NET SALARY = Payable Salary - Total Deductions + Total Additions
            const rawNetSalary = payableSalary - totalDed + totalAdditions;
            const netSalary = roundNetSalary(rawNetSalary);
            return netSalary.toFixed(2);
          },
          cellStyle: { 
            backgroundColor: '#e8f5e8', 
            fontWeight: 'bold', 
            color: '#2e7d32',
            border: '1px solid #4caf50',
            fontSize: '14px',
            padding: '8px 12px'
          }
        }
      ]
    }
  ], [EmployeeNameRenderer, CurrencyRenderer, StatusRenderer, getDaysInSelectedMonth]);

  // Default column properties
  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    floatingFilter: false,
    flex: 1,
    minWidth: 100,
    cellStyle: {
      display: 'flex',
      alignItems: 'center',
      paddingLeft: '6px',
      paddingRight: '6px',
      fontSize: '10px',
      lineHeight: '1.2',
      height: '32px'
    },
    headerClass: 'ag-header-custom',
    cellEditorParams: {
      style: {
        fontSize: '10px',
        padding: '4px 6px',
        minHeight: '24px',
        border: '2px solid #3b82f6',
        borderRadius: '4px',
        boxShadow: '0 1px 2px rgba(59, 130, 246, 0.1)'
      }
    }
  }), []);

  const onCellValueChanged = (params) => {
    console.log('Cell value changed:', params.colDef.field, 'to:', params.newValue);
    
    // Mark as having unsaved changes
    setHasUnsavedChanges(true);
    
    // Handle salary type changes to automatically update work days
    if (params.colDef.field === 'salaryType') {
      const newSalaryType = params.newValue;
      const daysInMonth = getDaysInSelectedMonth();
      let newWorkDays;
      
      if (newSalaryType === 'half-monthly') {
        newWorkDays = 15;
        console.log('Setting work days to 15 for half-monthly salary type');
      } else {
        newWorkDays = daysInMonth;
        console.log(`Setting work days to ${daysInMonth} for monthly salary type`);
      }
      
      // Update the work days field
      params.node.setDataValue('workDays', newWorkDays);
      
      // Show notification
      toast.info(`Work days automatically updated to ${newWorkDays} for ${newSalaryType} salary type`);
    }
    
    // Handle deduction/penalty amount changes - refresh calculations
    const deductionFields = ['sso', 'notCheckout', 'notCheckin', 'latePenalty', 'cut', 'advanceDeduction', 'pending', 'absentDays'];
    if (deductionFields.includes(params.colDef.field)) {
      console.log(`Deduction/penalty updated: ${params.colDef.field} = ${params.newValue}`);
      // The calculated columns will automatically update due to valueGetter functions
      toast.success(`${params.colDef.headerName} updated - Net salary recalculated`);
    }
    
    // Handle additional income changes - refresh calculations
    const incomeFields = ['bonus', 'overtime', 'otherIncome'];
    if (incomeFields.includes(params.colDef.field)) {
      console.log(`Additional income updated: ${params.colDef.field} = ${params.newValue}`);
      // The calculated columns will automatically update due to valueGetter functions
      toast.success(`${params.colDef.headerName} updated - Net salary recalculated`);
    }
    
    // Refresh the grid to update calculated fields
    params.api.refreshCells();
    
    // CRITICAL FIX: Sync grid data back to React state to trigger summary recalculation
    // This ensures the payrollSummary useMemo recalculates with updated data
    const updatedRowData = [];
    params.api.forEachNode((node) => {
      updatedRowData.push(node.data);
    });
    setPayrollData(updatedRowData);
    console.log('✅ Payroll data state synchronized with grid changes');
  };

  const onGridReady = (params) => {
    console.log('Grid is ready with data length:', payrollData.length);
    // Force refresh and redraw
    setTimeout(() => {
      if (params.api) {
        params.api.refreshCells({ force: true });
        params.api.redrawRows();
        params.api.sizeColumnsToFit();
        console.log('Grid refreshed and redrawn');
      }
    }, 200);
  };

  const exportToExcel = () => {
    try {
      if (!payrollData || payrollData.length === 0) {
        toast.warning('No payroll data available to export');
        return;
      }

      // Get department name
      const department = departments.find(d => d.id === parseInt(selectedDepartment));
      const departmentName = department ? department.department_name : 'All Departments';
      
      // Get month name
      const monthDate = new Date(selectedMonth + '-01');
      const monthName = monthDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      
      // Create workbook
      const workbook = XLSX.utils.book_new();
      
      // Prepare sheet with header
      const worksheetData = [];
      
      // Add company header
      worksheetData.push(['PAYROLL REPORT']);
      worksheetData.push([]);
      worksheetData.push(['Company', 'Jannaat Corp.']);
      worksheetData.push(['Department', departmentName]);
      worksheetData.push(['Period', monthName]);
      worksheetData.push(['Generated Date', new Date().toLocaleDateString()]);
      worksheetData.push([]);
      
      // Add summary statistics
      worksheetData.push(['SUMMARY STATISTICS']);
      worksheetData.push(['Total Employees', payrollData.length]);
      worksheetData.push(['Total Monthly Salary', payrollSummary.totalMonthlySalary]);
      worksheetData.push(['Total Gross Salary', payrollSummary.totalGrossSalary]);
      worksheetData.push(['Total Deductions', payrollSummary.totalDeductions]);
      worksheetData.push(['Total Net Salary', payrollSummary.totalNetSalary]);
      worksheetData.push(['Total Bonus', payrollSummary.totalBonus]);
      worksheetData.push(['Total Overtime', payrollSummary.totalOvertime]);
      worksheetData.push([]);
      
      // Add table header
      const headers = [
        'Employee Name',
        'Employee ID',
        'Position',
        'Salary Type',
        'Monthly Salary',
        'Per Day Salary',
        'Work Days',
        'Absent Days',
        'Late Days',
        'SSO',
        'Not Checkout',
        'Not Checkin',
        'Late Penalty',
        'Pending',
        'Cut',
        'Advance',
        'Bonus',
        'Overtime',
        'Other Income',
        'Gross Salary',
        'Total Deductions',
        'Net Salary',
        'Notes'
      ];
      
      worksheetData.push(headers);
      
      // Add employee data
      payrollData.forEach(record => {
        worksheetData.push([
          record.employeeName || '',
          record.employeeId || '',
          record.position || '',
          record.salaryType || '',
          record.monthlySalary || 0,
          record.perDaySalary || 0,
          record.workDays || 0,
          record.absentDays || 0,
          record.lateDays || 0,
          record.sso || 0,
          record.notCheckout || 0,
          record.notCheckin || 0,
          record.latePenalty || 0,
          record.pending || 0,
          record.cut || 0,
          record.advanceDeduction || 0,
          record.bonus || 0,
          record.overtime || 0,
          record.otherIncome || 0,
          calculateGrossSalary(record),
          calculateTotalDeductions(record),
          calculateNetSalary(record),
          record.noteRemark || ''
        ]);
      });
      
      // Create worksheet
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      
      // Set column widths
      worksheet['!cols'] = [
        { wch: 18 }, // Employee Name
        { wch: 12 }, // Employee ID
        { wch: 15 }, // Position
        { wch: 12 }, // Salary Type
        { wch: 12 }, // Monthly Salary
        { wch: 12 }, // Per Day Salary
        { wch: 10 }, // Work Days
        { wch: 10 }, // Absent Days
        { wch: 10 }, // Late Days
        { wch: 10 }, // SSO
        { wch: 12 }, // Not Checkout
        { wch: 12 }, // Not Checkin
        { wch: 12 }, // Late Penalty
        { wch: 10 }, // Pending
        { wch: 8 },  // Cut
        { wch: 10 }, // Advance
        { wch: 10 }, // Bonus
        { wch: 10 }, // Overtime
        { wch: 12 }, // Other Income
        { wch: 12 }, // Gross Salary
        { wch: 14 }, // Total Deductions
        { wch: 12 }, // Net Salary
        { wch: 20 }  // Notes
      ];
      
      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Payroll Report');
      
      // Save the file
      const fileName = `Payroll_${departmentName}_${selectedMonth}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      
      toast.success('Payroll data exported to Excel successfully!');
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Failed to export Excel file');
    }
  };

  const exportToPDF = () => {
    try {
      if (!payrollData || payrollData.length === 0) {
        toast.warning('No payroll data available to export');
        return;
      }

      // Get department name
      const department = departments.find(d => d.id === parseInt(selectedDepartment));
      const departmentName = department ? department.department_name : 'All Departments';
      
      // Get month name
      const monthDate = new Date(selectedMonth + '-01');
      const monthName = monthDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      
      // Create PDF in landscape mode
      const doc = new jsPDF('landscape');
      
      // Set fonts
      const pageHeight = doc.internal.pageSize.getHeight();
      const pageWidth = doc.internal.pageSize.getWidth();
      
      let yPosition = 10;
      
      // Add company header
      doc.setFontSize(16);
      doc.text('PAYROLL REPORT', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 10;
      
      // Add company info box
      doc.setFontSize(10);
      doc.text('Company: Jannaat Corp.', 14, yPosition);
      doc.text(`Department: ${departmentName}`, 14, yPosition + 5);
      doc.text(`Period: ${monthName}`, 14, yPosition + 10);
      doc.text(`Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`, 14, yPosition + 15);
      
      yPosition += 22;
      
      // Add summary statistics
      doc.setFontSize(11);
      doc.text('SUMMARY STATISTICS', 14, yPosition);
      yPosition += 7;
      
      const summaryData = [
        ['Total Employees', payrollData.length],
        ['Total Monthly Salary', `฿${payrollSummary.totalMonthlySalary.toLocaleString('th-TH', { minimumFractionDigits: 2 })}`],
        ['Total Gross Salary', `฿${payrollSummary.totalGrossSalary.toLocaleString('th-TH', { minimumFractionDigits: 2 })}`],
        ['Total Deductions', `฿${payrollSummary.totalDeductions.toLocaleString('th-TH', { minimumFractionDigits: 2 })}`],
        ['Total Net Salary', `฿${payrollSummary.totalNetSalary.toLocaleString('th-TH', { minimumFractionDigits: 2 })}`],
        ['Total Bonus', `฿${payrollSummary.totalBonus.toLocaleString('th-TH', { minimumFractionDigits: 2 })}`],
        ['Total Overtime', `฿${payrollSummary.totalOvertime.toLocaleString('th-TH', { minimumFractionDigits: 2 })}`]
      ];
      
      doc.autoTable({
        head: [['Description', 'Amount']],
        body: summaryData,
        startY: yPosition,
        margin: { left: 14, right: 14 },
        theme: 'grid',
        styles: { fontSize: 9, cellPadding: 3 },
        headStyles: { fillColor: [66, 139, 202], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 245, 245] }
      });
      
      yPosition = doc.lastAutoTable.finalY + 10;
      
      // Add payroll details table
      doc.setFontSize(11);
      doc.text('DETAILED PAYROLL RECORDS', 14, yPosition);
      yPosition += 7;
      
      const payrollTableHeaders = [
        'Employee',
        'ID',
        'Position',
        'Salary Type',
        'Monthly',
        'Per Day',
        'Work',
        'Absent',
        'Late',
        'SSO',
        'Deductions',
        'Bonus',
        'OT',
        'Gross',
        'Deduct',
        'Net'
      ];
      
      const payrollTableRows = payrollData.map(record => [
        (record.employeeName || '').substring(0, 15),
        record.employeeId || '',
        (record.position || '').substring(0, 10),
        record.salaryType || '',
        `฿${(record.monthlySalary || 0).toLocaleString('th-TH', { maximumFractionDigits: 0 })}`,
        `฿${(record.perDaySalary || 0).toLocaleString('th-TH', { maximumFractionDigits: 0 })}`,
        record.workDays || 0,
        record.absentDays || 0,
        record.lateDays || 0,
        `฿${(record.sso || 0).toLocaleString('th-TH', { maximumFractionDigits: 0 })}`,
        `฿${(calculateTotalDeductions(record) || 0).toLocaleString('th-TH', { maximumFractionDigits: 0 })}`,
        `฿${(record.bonus || 0).toLocaleString('th-TH', { maximumFractionDigits: 0 })}`,
        `฿${(record.overtime || 0).toLocaleString('th-TH', { maximumFractionDigits: 0 })}`,
        `฿${(calculateGrossSalary(record) || 0).toLocaleString('th-TH', { maximumFractionDigits: 0 })}`,
        `฿${(calculateTotalDeductions(record) || 0).toLocaleString('th-TH', { maximumFractionDigits: 0 })}`,
        `฿${(calculateNetSalary(record) || 0).toLocaleString('th-TH', { maximumFractionDigits: 0 })}`
      ]);
      
      doc.autoTable({
        head: [payrollTableHeaders],
        body: payrollTableRows,
        startY: yPosition,
        margin: { left: 14, right: 14 },
        theme: 'striped',
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [250, 250, 250] },
        didDrawPage: (data) => {
          // Add footer on each page
          const pageSize = doc.internal.pageSize;
          const pageHeight = pageSize.getHeight();
          const pageWidth = pageSize.getWidth();
          const pageNumber = doc.internal.getNumberOfPages();
          
          doc.setFontSize(8);
          doc.text(
            `Page ${pageNumber} | Generated on ${new Date().toLocaleDateString()}`,
            pageWidth / 2,
            pageHeight - 10,
            { align: 'center' }
          );
        }
      });
      
      // Save the PDF
      const fileName = `Payroll_${departmentName}_${selectedMonth}.pdf`;
      doc.save(fileName);
      
      toast.success('Payroll data exported to PDF successfully!');
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      toast.error('Failed to export PDF file');
    }
  };

  // Helper functions for salary calculations
  const calculateGrossSalary = (record) => {
    const baseSalary = record.monthlySalary || 0;
    const bonus = record.bonus || 0;
    const overtime = record.overtime || 0;
    const otherIncome = record.otherIncome || 0;
    return baseSalary + bonus + overtime + otherIncome;
  };

  const calculateTotalDeductions = (record) => {
    return (record.sso || 0) + (record.notCheckout || 0) + (record.notCheckin || 0) + 
           (record.latePenalty || 0) + (record.pending || 0) + (record.cut || 0) + 
           (record.advanceDeduction || 0);
  };

  const calculateNetSalary = (record) => {
    const rawNetSalary = calculateGrossSalary(record) - calculateTotalDeductions(record);
    return roundNetSalary(rawNetSalary);
  };

  // Check if payroll batch already exists for this department and month
  const checkExistingBatch = async () => {
    try {
      if (!selectedDepartment || !selectedMonth) return false;
      
      const [year, month] = selectedMonth.split('-');
      // Use simple version of batches endpoint
      const response = await axios.get(`/batches?year=${year}&department_id=${selectedDepartment}&month=${month}`, getHeaders());
      
      if (response.status === 200 && response.data.success && response.data.data.batches) {
        const existingBatch = response.data.data.batches.find(batch => 
          batch.payroll_month === selectedMonth && batch.department_id === parseInt(selectedDepartment)
        );
        return existingBatch ? existingBatch : false;
      }
      return false;
    } catch (error) {
      console.warn('Could not check for existing batch:', error);
      if (handleJWTError(error)) return false;
      return false;
    }
  };

  const savePayrollData = async () => {
    try {
      // Validate required data
      if (!selectedDepartment) {
        toast.error('Please select a department first');
        return;
      }
      
      if (!selectedMonth) {
        toast.error('Please select a payroll month');
        return;
      }
      
      if (!selectedPeriod) {
        toast.error('Please select a payroll period');
        return;
      }
      
      if (!payrollData || payrollData.length === 0) {
        toast.error('No payroll data to save');
        return;
      }

      // Validate payroll data integrity
      const invalidRecords = payrollData.filter(record => !record.id || isNaN(parseInt(record.id)));
      if (invalidRecords.length > 0) {
        console.error('Invalid records found:', invalidRecords);
        toast.error(`Found ${invalidRecords.length} records with missing or invalid employee IDs. Please refresh the employee data and try again.`);
        return;
      }

      console.log('Payroll data validation passed:', {
        totalRecords: payrollData.length,
        firstRecord: payrollData[0],
        allHaveValidIds: payrollData.every(r => r.id && !isNaN(parseInt(r.id))),
        selectedPeriod: selectedPeriod
      });

      // Save to local storage immediately
      saveToLocalStorage(payrollData, false);
      
      // Show loading state
      setLoading(true);

      // ============================================================
      // SINGLE-PERIOD PAYROLL PROCESSING
      // ============================================================
      console.log('╔═══════════════════════════════════════════════════════════════╗');
      console.log('║        SINGLE-PERIOD PAYROLL SAVE - USER SELECTED PERIOD      ║');
      console.log('╚═══════════════════════════════════════════════════════════════╝');
      
      // Extract year and month
      const [year, monthStr] = selectedMonth.split('-');
      const month = parseInt(monthStr, 10);
      
      console.log(`\n📅 Processing Payroll: ${year}-${String(month).padStart(2, '0')}`);
      console.log(`🏢 Department: ${departments.find(d => d.id === selectedDepartment)?.department_name || 'Unknown'} (ID: ${selectedDepartment})`);
      console.log(`📅 Selected Period: ${formatPeriodLabel(selectedPeriod)}`);
      console.log(`👥 Total Employees in Grid: ${payrollData.length}\n`);
      
      // Get period dates
      const periodDates = getPeriodDates(year, month, selectedPeriod);
      console.log(`📆 Period Dates: ${periodDates.start_date} to ${periodDates.end_date}\n`);
      
      // Filter employees based on salary type and selected period
      let employeesToSave = [];
      
      if (selectedPeriod === 'full-month') {
        // For full-month: Include ONLY monthly employees
        employeesToSave = payrollData.filter(emp => {
          const salaryType = (emp.salaryType || 'monthly').toLowerCase();
          return salaryType === 'monthly' || salaryType === 'month';
        });
        console.log(`✅ Full-month selected: Filtered to ${employeesToSave.length} monthly employees`);
      } else {
        // For 1-15 or 16-30: Include ONLY half-monthly employees
        employeesToSave = payrollData.filter(emp => {
          const salaryType = (emp.salaryType || '').toLowerCase();
          return salaryType === 'half-monthly' || salaryType === 'half monthly' || salaryType === 'halfmonthly';
        });
        console.log(`✅ ${formatPeriodLabel(selectedPeriod)} selected: Filtered to ${employeesToSave.length} half-monthly employees`);
      }
      
      if (employeesToSave.length === 0) {
        const periodLabel = formatPeriodLabel(selectedPeriod);
        if (selectedPeriod === 'full-month') {
          toast.warning(`No monthly employees found in the current data. Full-month payroll is only for monthly-salaried employees.`);
        } else {
          toast.warning(`No half-monthly employees found in the current data. Period ${periodLabel} is only for half-monthly salaried employees.`);
        }
        setLoading(false);
        return;
      }
      
      console.log(`\n📋 Employees to Save for ${formatPeriodLabel(selectedPeriod)}:`);
      employeesToSave.forEach((emp, idx) => {
        console.log(`  ${idx + 1}. ${emp.employeeName} (${emp.salaryType})`);
      });
      console.log('');
      
      // Check for duplicates for the selected period
      console.log(`🔍 Checking for existing payroll records...`);
      const duplicates = await checkForDuplicatePayroll(
        selectedDepartment,
        selectedMonth,
        selectedPeriod
      );
      
      console.log(`Found ${duplicates.length} existing records for ${formatPeriodLabel(selectedPeriod)}\n`);
      
      // Handle duplicates
      let shouldProceed = true;
      if (duplicates.length > 0) {
        const departmentName = departments.find(d => d.id === selectedDepartment)?.department_name || 'Unknown';
        const monthName = new Date(selectedMonth + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
        const periodLabel = formatPeriodLabel(selectedPeriod);
        
        let message = `⚠️ Existing payroll found for:\n\n`;
        message += `Department: ${departmentName}\n`;
        message += `Month: ${monthName}\n`;
        message += `Period: ${periodLabel}\n`;
        message += `Records: ${duplicates.length}\n\n`;
        message += 'Do you want to update the existing records?';
        
        shouldProceed = window.confirm(message);
        
        if (!shouldProceed) {
          toast.info('Save operation cancelled by user');
          setLoading(false);
          return;
        }
        
        console.log('⚠️ User confirmed update of existing payroll records');
      } else {
        console.log('✅ No duplicate payroll found - proceeding with creation');
      }
      
      // ============================================================
      // PROCESS SINGLE SELECTED PERIOD
      // ============================================================
      console.log(`\n🔄 Processing ${formatPeriodLabel(selectedPeriod)} payroll...\n`);
      console.log(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
      console.log(`🔹 Period: ${formatPeriodLabel(selectedPeriod)}`);
      console.log(`🔹 Employees: ${employeesToSave.length}`);
      console.log(`🔹 Period Dates: ${periodDates.start_date} to ${periodDates.end_date}`);
      console.log(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
      
      // Prepare data for API with comprehensive validation
      const payloadData = employeesToSave.map((record, index) => {
        // Validate required fields
        if (!record.id) {
          console.warn(`Record ${index}: Missing employee ID`, record);
        }
        if (!record.employeeId) {
          console.warn(`Record ${index}: Missing employeeId`, record);
        }
        if (!record.employeeName) {
          console.warn(`Record ${index}: Missing employeeName`, record);
        }

        // Calculate computed values to ensure they exist
        const monthlySalary = parseFloat(record.monthlySalary) || 0;
        const salaryType = record.salaryType || 'Monthly';
        const numberOfDays = parseInt(record.numberOfDays) || getDaysInSelectedMonth();
        const workDays = parseInt(record.workDays) || getDaysInSelectedMonth();
        
        // Calculate per day salary
        const perDaySalary = monthlySalary / getDaysInSelectedMonth();
        
        // Parse all deduction fields
        const ssoDeduction = parseFloat(record.sso) || 0;
        const notCheckoutPenalty = parseFloat(record.notCheckout) || 0;
        const notCheckinPenalty = parseFloat(record.notCheckin) || 0;
        const latePenaltyAmount = parseFloat(record.latePenalty) || 0;
        const pendingDeduction = parseFloat(record.pending) || 0;
        const cutDeduction = parseFloat(record.cut) || 0;
        const advanceDeduction = parseFloat(record.advanceDeduction) || 0;
        const absentDays = parseInt(record.absentDays) || 0;
        
        // Parse additional income fields
        const bonus = parseFloat(record.bonus) || 0;
        const overtime = parseFloat(record.overtime) || 0;
        const otherIncome = parseFloat(record.otherIncome) || 0;
        
        // CALCULATE BASE SALARY (matches backend calculateGrossSalary logic)
        let baseSalary = 0;
        
        // Debug logging for salary type matching
        console.log(`Employee ${record.employeeName}: salaryType="${salaryType}", monthlySalary=${monthlySalary}, workDays=${workDays}`);
        
        if (salaryType === 'daily') {
          baseSalary = perDaySalary * workDays;
          console.log(`  → Daily calculation: ${perDaySalary} × ${workDays} = ${baseSalary}`);
        } else if (salaryType === 'half-monthly') {
          // CRITICAL: exactly HALF of monthly salary (matches backend fix)
          baseSalary = monthlySalary / 2;
          console.log(`  → Half-monthly calculation: ${monthlySalary} ÷ 2 = ${baseSalary}`);
        } else {
          // Monthly (default)
          baseSalary = monthlySalary;
          console.log(`  → Monthly calculation: baseSalary = ${baseSalary}, salaryType="${salaryType}"`);
        }
        
        // CALCULATE GROSS SALARY (base + all additions)
        const grossSalary = baseSalary + bonus + overtime + otherIncome;
        console.log(`  → Gross Salary: ${baseSalary} + ${bonus} + ${overtime} + ${otherIncome} = ${grossSalary}`);
        
        // CALCULATE TOTAL DEDUCTIONS (matches backend calculateTotalDeductions logic)
        const absentDeduction = absentDays * perDaySalary;
        const totalDeductions = ssoDeduction + notCheckoutPenalty + notCheckinPenalty + 
                                latePenaltyAmount + pendingDeduction + cutDeduction + 
                                advanceDeduction + absentDeduction;
        
        // CALCULATE NET SALARY (gross - deductions)
        const rawNetSalary = grossSalary - totalDeductions;
        
        // ROUND NET SALARY to nearest 5 or 10
        const netSalary = roundNetSalary(rawNetSalary);
        
        console.log(`  → Net Salary: ${rawNetSalary.toFixed(2)} rounded to ${netSalary.toFixed(2)}`);
        
        // Ensure all numeric fields have valid values
        const validatedRecord = {
          // Employee identifiers (required) - map to correct database fields
          employee_id: parseInt(record.id) || null, // Use the primary employee ID (numeric)
          employee_name: record.employeeName || `Employee ${record.id || index + 1}`,
          employee_number: record.employeeId || `EMP${String(record.id).padStart(3, '0')}`, // Employee badge/number
          position: record.position || 'N/A',
          
          // Salary information (required) - map to correct database fields
          salary_type: salaryType.toLowerCase().replace('-', '-'), // Convert to match enum values
          monthly_salary: monthlySalary,
          per_day_salary: parseFloat(perDaySalary.toFixed(2)),
          
          // PERIOD INFORMATION (CRITICAL - must match batch period)
          payroll_period: selectedPeriod,
          period_start_date: periodDates.start_date,
          period_end_date: periodDates.end_date,
          
          // Attendance (required) - map to correct database fields
          total_days_in_month: numberOfDays,
          work_days: workDays,
          absent_days: absentDays,
          late_days: parseFloat(record.lateDays) || 0,
          
          // Deductions (optional, default to 0) - map to correct database fields
          sso_deduction: ssoDeduction,
          not_checkout_penalty: notCheckoutPenalty,
          not_checkin_penalty: notCheckinPenalty,
          late_penalty: latePenaltyAmount,
          pending_deduction: pendingDeduction,
          cut_deduction: cutDeduction,
          advance_deduction: advanceDeduction,
          
          // Additional income (optional, default to 0) - map to correct database fields
          bonus: bonus,
          overtime: overtime,
          other_income: otherIncome,
          
          // CALCULATED FIELDS (REQUIRED BY BACKEND)
          gross_salary: parseFloat(grossSalary.toFixed(2)),
          total_deductions: parseFloat(totalDeductions.toFixed(2)),
          net_salary: parseFloat(netSalary.toFixed(2)),
          
          // Notes (optional) - map to correct database field
          note_remark: record.noteRemark || ''
        };

        // Validate critical fields with proper employee ID
        if (!validatedRecord.employee_id || validatedRecord.employee_id === 0 || isNaN(validatedRecord.employee_id)) {
          console.error(`Invalid employee_id at index ${index}:`, validatedRecord);
          console.error('Original record:', record);
          throw new Error(`Employee ID missing or invalid for record ${index + 1} (${record.employeeName}). Expected numeric ID but got: ${validatedRecord.employee_id}`);
        }

        return validatedRecord;
      });

      // Log payload data for debugging
      console.log('Payload Data Summary:', {
        totalRecords: payloadData.length,
        employeeIds: payloadData.map(r => r.employee_id),
        hasAllIds: payloadData.every(r => r.employee_id && r.employee_id !== 0),
        sampleRecord: payloadData[0],
        // Verify calculated fields are present
        hasGrossSalary: payloadData.every(r => r.gross_salary !== undefined),
        hasTotalDeductions: payloadData.every(r => r.total_deductions !== undefined),
        hasNetSalary: payloadData.every(r => r.net_salary !== undefined),
        sampleCalculations: payloadData[0] ? {
          gross_salary: payloadData[0].gross_salary,
          total_deductions: payloadData[0].total_deductions,
          net_salary: payloadData[0].net_salary
        } : null
      });

      // Additional validation
      if (payloadData.length === 0) {
        throw new Error('No valid payroll records to save');
      }

      // Check for duplicate employee IDs
      const employeeIds = payloadData.map(r => r.employee_id);
      const duplicateIds = employeeIds.filter((id, index) => employeeIds.indexOf(id) !== index);
      if (duplicateIds.length > 0) {
        console.warn('Duplicate employee IDs found:', duplicateIds);
        toast.warning(`Warning: Duplicate employee IDs detected: ${duplicateIds.join(', ')}`);
      }

      // Check for zero or null employee IDs
      const invalidIds = payloadData.filter(r => !r.employee_id || r.employee_id === 0 || isNaN(r.employee_id));
      if (invalidIds.length > 0) {
        console.error('Records with invalid employee IDs:', invalidIds);
        throw new Error(`Found ${invalidIds.length} records with invalid employee IDs. Please check your employee data and ensure all employees have valid numeric IDs.`);
      }
      
      // ============================================================
      // COMPREHENSIVE PAYLOAD DATA CONSOLE LOGGING
      // ============================================================
      console.log('\n');
      console.log('╔═══════════════════════════════════════════════════════════════╗');
      console.log('║         PAYROLL SAVE OPERATION - COMPLETE PAYLOAD DATA        ║');
      console.log('╚═══════════════════════════════════════════════════════════════╝');
      console.log('\n');
      
      // 1. OPERATION METADATA
      console.log('📋 OPERATION METADATA:');
      console.log('────────────────────────────────────────────────────────────────');
      console.log('Operation Type:', '✨ CREATE');
      console.log('Department ID:', selectedDepartment);
      console.log('Department Name:', departments.find(d => d.id === selectedDepartment)?.department_name || 'Unknown');
      console.log('Payroll Month:', selectedMonth);
      console.log('Payroll Period:', formatPeriodLabel(selectedPeriod));
      console.log('Period Dates:', `${periodDates.start_date} to ${periodDates.end_date}`);
      console.log('Total Records:', payloadData.length);
      console.log('Timestamp:', new Date().toISOString());
      console.log('\n');
      
      // 2. COMPLETE PAYLOAD DATA ARRAY
      console.log('📦 COMPLETE PAYLOAD DATA (ALL RECORDS):');
      console.log('────────────────────────────────────────────────────────────────');
      console.table(payloadData);
      console.log('\n');
      
      // 3. DETAILED RECORD-BY-RECORD BREAKDOWN
      console.log('📊 DETAILED RECORD BREAKDOWN:');
      console.log('────────────────────────────────────────────────────────────────');
      payloadData.forEach((record, index) => {
        console.log(`\n[Record ${index + 1}/${payloadData.length}] ${record.employee_name}`);
        console.log('  Employee Info:');
        console.log('    • ID:', record.employee_id);
        console.log('    • Number:', record.employee_number);
        console.log('    • Position:', record.position);
        console.log('  Salary Details:');
        console.log('    • Type:', record.salary_type);
        console.log('    • Monthly:', `฿${record.monthly_salary.toLocaleString()}`);
        console.log('    • Per Day:', `฿${record.per_day_salary.toLocaleString()}`);
        console.log('  ⚠️ CALCULATED FIELDS (SENT TO BACKEND):');
        console.log('    • GROSS SALARY:', `฿${record.gross_salary.toLocaleString()} ← CHECK THIS VALUE`);
        console.log('    • TOTAL DEDUCTIONS:', `฿${record.total_deductions.toLocaleString()}`);
        console.log('    • NET SALARY:', `฿${record.net_salary.toLocaleString()}`);
        console.log('  Attendance:');
        console.log('    • Total Days:', record.total_days_in_month);
        console.log('    • Work Days:', record.work_days);
        console.log('    • Absent Days:', record.absent_days);
        console.log('    • Late Days:', record.late_days);
        console.log('  Deductions:');
        console.log('    • SSO:', `฿${record.sso_deduction.toLocaleString()}`);
        console.log('    • Not Checkout:', `฿${record.not_checkout_penalty.toLocaleString()}`);
        console.log('    • Not Checkin:', `฿${record.not_checkin_penalty.toLocaleString()}`);
        console.log('    • Late Penalty:', `฿${record.late_penalty.toLocaleString()}`);
        console.log('    • Pending:', `฿${record.pending_deduction.toLocaleString()}`);
        console.log('    • Cut:', `฿${record.cut_deduction.toLocaleString()}`);
        console.log('    • Advance:', `฿${record.advance_deduction.toLocaleString()}`);
        const totalDeductions = record.sso_deduction + record.not_checkout_penalty + 
          record.not_checkin_penalty + record.late_penalty + record.pending_deduction + 
          record.cut_deduction + record.advance_deduction + (record.absent_days * record.per_day_salary);
        console.log('    • TOTAL Deductions:', `฿${totalDeductions.toLocaleString()}`);
        console.log('  Additional Income:');
        console.log('    • Bonus:', `฿${record.bonus.toLocaleString()}`);
        console.log('    • Overtime:', `฿${record.overtime.toLocaleString()}`);
        console.log('    • Other Income:', `฿${record.other_income.toLocaleString()}`);
        const totalAdditions = record.bonus + record.overtime + record.other_income;
        console.log('    • TOTAL Additions:', `฿${totalAdditions.toLocaleString()}`);
        console.log('  Note:', record.note_remark || '(none)');
      });
      console.log('\n');
      
      // 4. AGGREGATED STATISTICS
      console.log('📈 AGGREGATED STATISTICS:');
      console.log('────────────────────────────────────────────────────────────────');
      const stats = {
        totalEmployees: payloadData.length,
        totalMonthlySalary: payloadData.reduce((sum, r) => sum + r.monthly_salary, 0),
        totalGrossSalary: payloadData.reduce((sum, r) => sum + r.gross_salary, 0),
        totalDeductionsCalculated: payloadData.reduce((sum, r) => sum + r.total_deductions, 0),
        totalNetSalary: payloadData.reduce((sum, r) => sum + r.net_salary, 0),
        totalWorkDays: payloadData.reduce((sum, r) => sum + r.work_days, 0),
        totalAbsentDays: payloadData.reduce((sum, r) => sum + r.absent_days, 0),
        totalLateDays: payloadData.reduce((sum, r) => sum + r.late_days, 0),
        totalSSO: payloadData.reduce((sum, r) => sum + r.sso_deduction, 0),
        totalNotCheckout: payloadData.reduce((sum, r) => sum + r.not_checkout_penalty, 0),
        totalNotCheckin: payloadData.reduce((sum, r) => sum + r.not_checkin_penalty, 0),
        totalLatePenalty: payloadData.reduce((sum, r) => sum + r.late_penalty, 0),
        totalPending: payloadData.reduce((sum, r) => sum + r.pending_deduction, 0),
        totalCut: payloadData.reduce((sum, r) => sum + r.cut_deduction, 0),
        totalAdvance: payloadData.reduce((sum, r) => sum + r.advance_deduction, 0),
        totalBonus: payloadData.reduce((sum, r) => sum + r.bonus, 0),
        totalOvertime: payloadData.reduce((sum, r) => sum + r.overtime, 0),
        totalOtherIncome: payloadData.reduce((sum, r) => sum + r.other_income, 0),
      };
      console.log('Total Employees:', stats.totalEmployees);
      console.log('Total Monthly Salary:', `฿${stats.totalMonthlySalary.toLocaleString()}`);
      console.log('\n⚠️ CALCULATED TOTALS (SENT TO BACKEND):');
      console.log('  Total GROSS Salary:', `฿${stats.totalGrossSalary.toLocaleString()} ← SHOULD MATCH BACKEND`);
      console.log('  Total DEDUCTIONS:', `฿${stats.totalDeductionsCalculated.toLocaleString()} ← SHOULD MATCH BACKEND`);
      console.log('  Total NET Salary:', `฿${stats.totalNetSalary.toLocaleString()} ← SHOULD MATCH BACKEND`);
      console.log('\nTotal Work Days:', stats.totalWorkDays);
      console.log('Total Absent Days:', stats.totalAbsentDays);
      console.log('Total Late Days:', stats.totalLateDays);
      console.log('\nDeductions Breakdown:');
      console.log('  SSO:', `฿${stats.totalSSO.toLocaleString()}`);
      console.log('  Not Checkout:', `฿${stats.totalNotCheckout.toLocaleString()}`);
      console.log('  Not Checkin:', `฿${stats.totalNotCheckin.toLocaleString()}`);
      console.log('  Late Penalty:', `฿${stats.totalLatePenalty.toLocaleString()}`);
      console.log('  Pending:', `฿${stats.totalPending.toLocaleString()}`);
      console.log('  Cut:', `฿${stats.totalCut.toLocaleString()}`);
      console.log('  Advance:', `฿${stats.totalAdvance.toLocaleString()}`);
      const grandTotalDeductions = stats.totalSSO + stats.totalNotCheckout + stats.totalNotCheckin + 
        stats.totalLatePenalty + stats.totalPending + stats.totalCut + stats.totalAdvance;
      console.log('  GRAND TOTAL Deductions:', `฿${grandTotalDeductions.toLocaleString()}`);
      console.log('\nAdditional Income Breakdown:');
      console.log('  Bonus:', `฿${stats.totalBonus.toLocaleString()}`);
      console.log('  Overtime:', `฿${stats.totalOvertime.toLocaleString()}`);
      console.log('  Other Income:', `฿${stats.totalOtherIncome.toLocaleString()}`);
      const grandTotalAdditions = stats.totalBonus + stats.totalOvertime + stats.totalOtherIncome;
      console.log('  GRAND TOTAL Additions:', `฿${grandTotalAdditions.toLocaleString()}`);
      console.log('\n');
      
      // 5. VALIDATION SUMMARY
      console.log('✅ VALIDATION SUMMARY:');
      console.log('────────────────────────────────────────────────────────────────');
      const validation = {
        allHaveEmployeeId: payloadData.every(r => r.employee_id && !isNaN(r.employee_id)),
        allHaveEmployeeName: payloadData.every(r => r.employee_name && r.employee_name.trim() !== ''),
        allHaveSalaryType: payloadData.every(r => r.salary_type),
        allHaveMonthlySalary: payloadData.every(r => r.monthly_salary >= 0),
        employeeIds: payloadData.map(r => r.employee_id),
        duplicateIds: payloadData.map(r => r.employee_id).filter((id, idx, arr) => arr.indexOf(id) !== idx)
      };
      console.log('All Have Employee ID:', validation.allHaveEmployeeId ? '✅ YES' : '❌ NO');
      console.log('All Have Employee Name:', validation.allHaveEmployeeName ? '✅ YES' : '❌ NO');
      console.log('All Have Salary Type:', validation.allHaveSalaryType ? '✅ YES' : '❌ NO');
      console.log('All Have Valid Salary:', validation.allHaveMonthlySalary ? '✅ YES' : '❌ NO');
      console.log('Employee IDs:', validation.employeeIds);
      console.log('Duplicate IDs:', validation.duplicateIds.length > 0 ? validation.duplicateIds : 'None');
      console.log('\n');
      
      // 6. FINAL PAYLOAD OBJECT WITH PERIOD INFORMATION
      console.log('🚀 FINAL API PAYLOAD:');
      console.log('────────────────────────────────────────────────────────────────');
      const finalPayload = {
        department_id: selectedDepartment,
        payroll_month: selectedMonth,
        payroll_period: selectedPeriod,
        period_start_date: periodDates.start_date,
        period_end_date: periodDates.end_date,
        payroll_data: payloadData,
        created_by: 1
      };
      console.log(JSON.stringify(finalPayload, null, 2));
      console.log('\n');
      
      // Make API call for this period
      console.log(`📤 Sending payroll to API: ${formatPeriodLabel(selectedPeriod)}`);
      const response = await axios.post('/hr/save-records', finalPayload, getHeaders());
      
      const result = response.data;
      
      console.log('✅ Payroll saved successfully:', {
        period: selectedPeriod,
        batchNumber: result.data?.batch_number,
        recordsSaved: result.data?.records_saved || payloadData.length,
        totalNetSalary: result.data?.summary?.total_net_salary
      });
      
      console.log('\n╔═══════════════════════════════════════════════════════════════╗');
      console.log('║            PAYROLL SAVED SUCCESSFULLY                         ║');
      console.log('╚═══════════════════════════════════════════════════════════════╝');
      console.log(`✅ Period: ${formatPeriodLabel(selectedPeriod)}`);
      console.log(`✅ Batch Number: ${result.data?.batch_number || 'N/A'}`);
      console.log(`✅ Records Saved: ${result.data?.records_saved || payloadData.length}`);
      if (result.data?.summary?.total_net_salary) {
        console.log(`✅ Total Net Salary: ฿${result.data.summary.total_net_salary.toLocaleString()}`);
      }
      console.log('\n');
      
      // Show success message
      const departmentName = departments.find(d => d.id === selectedDepartment)?.department_name || 'Unknown';
      const monthName = new Date(selectedMonth + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      
      toast.success(
        <div>
          <strong>✅ Payroll saved successfully!</strong><br />
          <strong>{departmentName}</strong> - <strong>{monthName}</strong><br />
          <div style={{marginTop: '4px'}}>
            • {formatPeriodLabel(selectedPeriod)}: Batch {result.data?.batch_number || 'N/A'} ({payloadData.length} records)
          </div>
        </div>,
        { autoClose: 5000 }
      );
      
      // Clear unsaved changes flag
      setHasUnsavedChanges(false);
      
      // Optionally clear recovery data
      const storageKey = getStorageKey();
      localStorage.removeItem(storageKey);
      
      // Refresh payroll statistics after successful save
      setTimeout(() => {
        calculatePayrollStats();
      }, 1000);
      
    } catch (error) {
      console.error('Error saving payroll data:', error);
      
      // Check for JWT expiration or authentication errors (PRIORITY FIRST)
      if (handleJWTError(error)) {
        return;
      }
      
      // Check for different types of errors
      if (error.response && error.response.status === 409) {
        // Duplicate batch error
        toast.warning('⚠️ Payroll batch for this department and month already exists. Data will be updated.');
        
        // Still mark as successful since data was processed
        setHasUnsavedChanges(false);
        const storageKey = getStorageKey();
        localStorage.removeItem(storageKey);
        
        // Refresh payroll statistics
        setTimeout(() => {
          calculatePayrollStats();
        }, 1000);
        
      } else if (error.response && error.response.data && error.response.data.message) {
        // Backend validation or database errors
        const errorMessage = error.response.data.message;
        
        if (errorMessage.includes('Duplicate entry') && (errorMessage.includes('unique_batch_employee_period') || errorMessage.includes('unique_dept_month_period'))) {
          toast.warning('⚠️ Payroll for this period already exists. The duplicate checking should have caught this.');
        } else if (errorMessage.includes('basic_salary')) {
          toast.error('❌ Database schema mismatch: Please contact system administrator to update database structure.');
        } else {
          toast.error(`❌ Server error: ${errorMessage}`);
        }
      } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
        toast.error('❌ Network error: Please check your connection and try again');
      } else {
        toast.error(`❌ Failed to save payroll data: ${error.message || 'Unknown error occurred'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const recoverSavedData = () => {
    if (recoveryData && recoveryData.payrollData) {
      setPayrollData(recoveryData.payrollData);
      setSelectedDepartment(recoveryData.department);
      setSelectedMonth(recoveryData.month);
      setHasUnsavedChanges(true);
      setRecoveryData(null);
      setRecoveryToastShown(false); // Reset toast flag
      
      toast.success(
        <div>
          <UndoOutlined className="me-2" />
          <strong>Data Recovered!</strong><br />
          Restored from {new Date(recoveryData.timestamp).toLocaleString()}
        </div>
      );
    }
  };

  const discardRecoveryData = () => {
    clearLocalStorage();
    setRecoveryData(null);
    setRecoveryToastShown(false); // Reset toast flag
    toast.info('Recovery data discarded');
  };

  const manualSave = () => {
    if (payrollData.length > 0) {
      saveToLocalStorage(payrollData, false);
    } else {
      toast.warn('No data to save');
    }
  };

  return (
    <Layout>
      <Header title="Payroll Management" />
      
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: '#6f8ff7',
            borderRadius: 10,
            fontSize: 13,
          },
          algorithm: theme.defaultAlgorithm,
        }}
      >
        <AntLayout style={{ padding: '24px', background: '#f5f5f5', minHeight: '100vh' }}>
          {/* Control Panel */}
          <Card
            className="payroll-control-card"
            style={{ marginBottom: 24, boxShadow: '0 10px 30px rgba(67, 102, 176, 0.08)' }}
            bodyStyle={{ padding: '24px' }}
          >
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} sm={12} md={8} lg={6}>
                <div className="payroll-control-field">
                  <label className="payroll-control-label">
                    Select Department
                  </label>
                  <Select
                    placeholder="Choose Department"
                    value={selectedDepartment}
                    onChange={setSelectedDepartment}
                    className="payroll-soft-select"
                    style={{ width: '100%' }}
                    options={departments.map(dept => ({
                      label: dept.department_name,
                      value: dept.id
                    }))}
                    optionLabelProp="label"
                    size="large"
                  />
                </div>
              </Col>
              <Col xs={24} sm={12} md={8} lg={5}>
                <div className="payroll-control-field">
                  <label className="payroll-control-label">
                    <CalendarOutlined style={{ marginRight: 8 }} />
                    Payroll Month
                  </label>
                  <Input
                    className="payroll-soft-input"
                    type="month"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    size="large"
                  />
                </div>
              </Col>
              <Col xs={24} sm={24} md={8} lg={7}>
                <div className="payroll-control-field">
                  <label className="payroll-control-label">
                    Payroll Period
                  </label>
                  <Radio.Group
                    className="payroll-period-group"
                    value={selectedPeriod}
                    onChange={(e) => setSelectedPeriod(e.target.value)}
                    size="large"
                    buttonStyle="solid"
                    style={{ width: '100%' }}
                  >
                    <Radio.Button value="full-month" style={{ width: '33.33%', textAlign: 'center', fontSize: '11px', padding: '0 5px' }}>
                      Full Month
                    </Radio.Button>
                    <Radio.Button value="1-15" style={{ width: '33.33%', textAlign: 'center', fontSize: '11px', padding: '0 5px' }}>
                      Half 1-15
                    </Radio.Button>
                    <Radio.Button value="16-30" style={{ width: '33.33%', textAlign: 'center', fontSize: '11px', padding: '0 5px' }}>
                      Half 16-30
                    </Radio.Button>
                  </Radio.Group>
                  <small className="payroll-period-hint">
                    {selectedPeriod === 'full-month' ? 'Monthly employees' : 'Half-monthly employees'}
                  </small>
                </div>
              </Col>
              <Col xs={24} sm={24} md={24} lg={6}>
                <div className="payroll-top-actions-wrap">
                  <Space className="payroll-action-group" style={{ width: '100%', justifyContent: 'flex-end' }} wrap>
                    <Tooltip title="Save payroll data to database">
                      <Button
                        className="payroll-btn payroll-btn-primary"
                        type="primary"
                        icon={<SaveOutlined />}
                        onClick={savePayrollData}
                        disabled={!selectedDepartment || payrollData.length === 0}
                        size="large"
                      >
                        Save Payroll
                      </Button>
                    </Tooltip>
                    <Tooltip title="Quick save to local storage">
                      <Button
                        className="payroll-btn payroll-btn-soft"
                        type="dashed"
                        icon={<SaveOutlined />}
                        onClick={manualSave}
                        disabled={payrollData.length === 0}
                        size="large"
                      >
                        Quick Save
                      </Button>
                    </Tooltip>
                  </Space>
                </div>
              </Col>
            </Row>

            <Row gutter={[16, 16]} align="middle" style={{ marginTop: 16 }}>
              <Col xs={24}>
                <Space className="payroll-action-group" style={{ width: '100%', justifyContent: 'flex-end' }} wrap>
                  <Tooltip title="View salary summary for selected month and department">
                    <Button
                      className="payroll-btn payroll-btn-soft"
                      type="dashed"
                      icon={<EyeOutlined />}
                      onClick={() => setShowSalaryModal(true)}
                      disabled={!selectedDepartment || !selectedMonth || payrollData.length === 0}
                      size="large"
                    >
                      View Salary
                    </Button>
                  </Tooltip>
                  <Tooltip title="Export payroll data to PDF">
                    <Button
                      className="payroll-btn payroll-btn-pdf"
                      type="default"
                      danger
                      icon={<DownloadOutlined />}
                      onClick={exportToPDF}
                      disabled={!selectedDepartment || payrollData.length === 0}
                      size="large"
                    >
                      Export PDF
                    </Button>
                  </Tooltip>
                  <Tooltip title="Export payroll data to Excel file">
                    <Button
                      className="payroll-btn payroll-btn-excel"
                      type="default"
                      icon={<DownloadOutlined />}
                      onClick={exportToExcel}
                      disabled={!selectedDepartment || payrollData.length === 0}
                      size="large"
                      style={{ color: '#52c41a', borderColor: '#52c41a' }}
                    >
                      Export Excel
                    </Button>
                  </Tooltip>
                </Space>
              </Col>
            </Row>

            <Divider style={{ margin: '16px 0' }} />

            {/* Auto-save Control */}
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} sm={12}>
                <Space align="center">
                  <Switch
                    checked={autoSaveEnabled}
                    onChange={setAutoSaveEnabled}
                  />
                  <span style={{ fontWeight: 500 }}>Auto-save enabled (every 30s)</span>
                  {hasUnsavedChanges && (
                    <Tag color="orange">Unsaved changes</Tag>
                  )}
                  {lastSaveTime && (
                    <span style={{ fontSize: 12, color: '#999' }}>
                      Last saved: {lastSaveTime.toLocaleTimeString()}
                    </span>
                  )}
                </Space>
              </Col>
            </Row>

            {/* Recovery Banner */}
            {recoveryData && (
              <Alert
                message="Recovery Data Available"
                description={`Found unsaved changes from ${new Date(recoveryData.timestamp).toLocaleString()}`}
                type="warning"
                icon={<ExclamationCircleOutlined />}
                style={{ marginTop: 16 }}
                action={
                  <Space>
                    <Button size="small" type="primary" onClick={recoverSavedData}>
                      <UndoOutlined /> Recover
                    </Button>
                    <Button size="small" danger onClick={discardRecoveryData}>
                      <DeleteOutlined /> Discard
                    </Button>
                  </Space>
                }
              />
            )}

            {selectedDepartment && (
              <Alert
                message="💡 Quick Tips"
                description="Double-click cells to edit • Use filters to find employees • Export data for reporting • Auto-save protects your work"
                type="info"
                style={{ marginTop: 16 }}
              />
            )}
          </Card>

          {/* Payroll Progress Indicator */}
          {selectedMonth && (
            <Card
              className="payroll-progress-card"
              style={{ marginBottom: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
              bodyStyle={{ padding: '24px' }}
            >
              <Row gutter={[24, 24]} align="middle">
                <Col xs={24} md={16}>
                  <div>
                    <h6 style={{ marginBottom: 12, fontWeight: 600, color: '#262626' }}>
                      <UserOutlined style={{ marginRight: 8 }} />
                      Payroll Progress - {new Date(selectedMonth + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </h6>
                    <Progress 
                      percent={payrollStats.completionPercentage} 
                      strokeColor={{ '0%': '#722ed1', '100%': '#13c2c2' }}
                      status={payrollStats.completionPercentage === 100 ? 'success' : 'active'}
                    />
                    <Space style={{ marginTop: 12, width: '100%', justifyContent: 'space-between', fontSize: 12 }}>
                      <span style={{ color: '#999' }}>
                        {payrollStats.completedPayroll} of {payrollStats.totalEmployees} employees
                      </span>
                      <span style={{ fontWeight: 600, color: '#722ed1' }}>
                        {payrollStats.completionPercentage}% Complete
                      </span>
                    </Space>
                  </div>
                </Col>
                <Col xs={24} md={8}>
                  <Row gutter={[16, 16]}>
                    <Col xs={12} sm={8}>
                      <Statistic
                        title="Completed"
                        value={payrollStats.completedPayroll}
                        valueStyle={{ color: '#52c41a', fontSize: 24, fontWeight: 600 }}
                      />
                    </Col>
                    <Col xs={12} sm={8}>
                      <Statistic
                        title="Remaining"
                        value={payrollStats.remainingPayroll}
                        valueStyle={{ color: '#faad14', fontSize: 24, fontWeight: 600 }}
                      />
                    </Col>
                    {payrollStats.remainingPayroll > 0 && (
                      <Col xs={24} sm={8}>
                        <Button
                          type="dashed"
                          icon={<EyeOutlined />}
                          onClick={() => setShowEmployeeModal(true)}
                          block
                        >
                          View List
                        </Button>
                      </Col>
                    )}
                  </Row>
                </Col>
              </Row>
            </Card>
          )}

          {/* Statistics Cards */}
          {selectedDepartment && (
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={12} md={6}>
                <Card
                  style={{
                    borderLeft: '4px solid #722ed1',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  }}
                  bodyStyle={{ padding: '16px' }}
                >
                  <Statistic
                    title="Total Employees"
                    value={payrollSummary.totalEmployees}
                    prefix={<UserOutlined />}
                    valueStyle={{ color: '#722ed1', fontSize: 28, fontWeight: 600 }}
                  />
                  <small style={{ color: '#999' }}>Active workforce</small>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card
                  style={{
                    borderLeft: '4px solid #1890ff',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  }}
                  bodyStyle={{ padding: '16px' }}
                >
                  <Statistic
                    title="Total Gross Salary"
                    value={payrollSummary.totalGrossSalary}
                    valueStyle={{ color: '#1890ff', fontSize: 24, fontWeight: 600 }}
                    formatter={(value) => `฿${(value).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  />
                  <small style={{ color: '#999' }}>
                    Base + Bonus (฿{payrollSummary.totalBonus.toLocaleString('th-TH')}) + 
                    OT (฿{payrollSummary.totalOvertime.toLocaleString('th-TH')}) + 
                    Other (฿{payrollSummary.totalOtherIncome.toLocaleString('th-TH')})
                  </small>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card
                  style={{
                    borderLeft: '4px solid #ff4d4f',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  }}
                  bodyStyle={{ padding: '16px' }}
                >
                  <Statistic
                    title="Total Deductions"
                    value={payrollSummary.totalDeductions}
                    valueStyle={{ color: '#ff4d4f', fontSize: 24, fontWeight: 600 }}
                    formatter={(value) => `฿${(value).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  />
                  <small style={{ color: '#999' }}>
                    SSO + Penalties + Advances + Absences ({payrollSummary.totalAbsentDays} days)
                  </small>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card
                  style={{
                    borderLeft: '4px solid #52c41a',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  }}
                  bodyStyle={{ padding: '16px' }}
                >
                  <Statistic
                    title="Total Net Salary"
                    value={payrollSummary.totalNetSalary}
                    valueStyle={{ color: '#52c41a', fontSize: 24, fontWeight: 600 }}
                    formatter={(value) => `฿${(value).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  />
                  <small style={{ color: '#999' }}>Final payable amount</small>
                </Card>
              </Col>
            </Row>
          )}

          {/* Grid Container */}
          {selectedDepartment ? (
            <Card
              style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
              bodyStyle={{ padding: 0 }}
              className="payroll-grid-card"
            >
              <div className={`ag-theme-quartz payroll-grid-container`} style={{ height: '600px', width: '100%' }}>
                <AgGridReact
                  ref={gridRef}
                  key={gridKey}
                  rowData={payrollData}
                  columnDefs={columnDefs}
                  getRowClass={getPayrollRowClass}
                  defaultColDef={{
                    flex: 1,
                    minWidth: 80,
                    resizable: true,
                    editable: true,
                    cellStyle: { fontSize: '12px', paddingLeft: '8px', paddingRight: '8px' },
                    cellClass: 'payroll-cell',
                    tooltipShowDelay: 450
                  }}
                  onCellValueChanged={onCellValueChanged}
                  onCellClicked={handleCellClicked}
                  onCellFocused={handleCellFocused}
                  pagination={true}
                  paginationPageSize={15}
                  paginationPageSizeSelector={[10, 15, 20, 50]}
                  suppressRowClickSelection={false}
                  animateRows={true}
                  domLayout="normal"
                  theme="ag-theme-quartz"
                />
              </div>
            </Card>
          ) : (
            <Card
              style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
              bodyStyle={{ padding: '60px 24px' }}
            >
              <Empty
                description="No Department Selected"
                style={{ marginBottom: 0 }}
              >
                <p style={{ color: '#999', marginTop: 16 }}>
                  Select a department from the dropdown above to view and manage payroll data.
                </p>
                <Row gutter={[16, 16]} style={{ marginTop: 32 }} justify="center">
                  <Col>
                    <Card
                      style={{
                        textAlign: 'center',
                        borderLeft: '4px solid #722ed1',
                        width: 150,
                      }}
                      bodyStyle={{ padding: '16px' }}
                    >
                      <div style={{ fontSize: 24, marginBottom: 8 }}>✏️</div>
                      <small style={{ fontWeight: 500 }}>Edit employee data</small>
                    </Card>
                  </Col>
                  <Col>
                    <Card
                      style={{
                        textAlign: 'center',
                        borderLeft: '4px solid #1890ff',
                        width: 150,
                      }}
                      bodyStyle={{ padding: '16px' }}
                    >
                      <div style={{ fontSize: 24, marginBottom: 8 }}>📥</div>
                      <small style={{ fontWeight: 500 }}>Export to Excel</small>
                    </Card>
                  </Col>
                  <Col>
                    <Card
                      style={{
                        textAlign: 'center',
                        borderLeft: '4px solid #52c41a',
                        width: 150,
                      }}
                      bodyStyle={{ padding: '16px' }}
                    >
                      <div style={{ fontSize: 24, marginBottom: 8 }}>💰</div>
                      <small style={{ fontWeight: 500 }}>Calculate salaries</small>
                    </Card>
                  </Col>
                </Row>
              </Empty>
            </Card>
          )}

          {/* Employees Without Payroll Modal */}
          <Modal
            title={
              <Space>
                <UserOutlined />
                Employees Without Payroll
                {selectedMonth && ` - ${new Date(selectedMonth + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`}
              </Space>
            }
            open={showEmployeeModal}
            onCancel={() => setShowEmployeeModal(false)}
            footer={null}
            width={900}
            centered
          >
            {employeesWithoutPayroll.length === 0 ? (
              <Empty
                description="All Employees Completed"
                style={{ marginBottom: 0, marginTop: 40 }}
              >
                <p style={{ color: '#999', marginTop: 16 }}>
                  All employees have payroll records for this month.
                </p>
              </Empty>
            ) : (
              <>
                <Alert
                  message={`${employeesWithoutPayroll.length} Employees Remaining`}
                  description={`These employees need payroll processing for ${new Date(selectedMonth + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`}
                  type="warning"
                  style={{ marginBottom: 16 }}
                  icon={<ExclamationCircleOutlined />}
                />
                <Table
                  rowKey="id"
                  columns={[
                    {
                      title: 'Employee Name',
                      dataIndex: 'employee_name',
                      key: 'employee_name',
                      sorter: (a, b) => {
                        const nameA = a.english_first_name || a.thai_first_name || '';
                        const nameB = b.english_first_name || b.thai_first_name || '';
                        return nameA.localeCompare(nameB);
                      },
                      sortDirections: ['ascend', 'descend', 'ascend'],
                      render: (text, record) =>
                        record.english_first_name && record.english_last_name
                          ? `${record.english_first_name} ${record.english_last_name}`
                          : record.thai_first_name && record.thai_last_name
                          ? `${record.thai_first_name} ${record.thai_last_name}`
                          : `Employee ${record.id}`,
                    },
                    {
                      title: 'Department',
                      dataIndex: 'department_name',
                      key: 'department_name',
                      sorter: (a, b) => (a.department_name || '').localeCompare(b.department_name || ''),
                      sortDirections: ['ascend', 'descend', 'ascend'],
                      render: (text) => <Tag color="blue">{text || 'Unknown'}</Tag>,
                    },
                    {
                      title: 'Position',
                      dataIndex: 'position_name',
                      key: 'position_name',
                      sorter: (a, b) => (a.position_name || '').localeCompare(b.position_name || ''),
                      sortDirections: ['ascend', 'descend', 'ascend'],
                      render: (text) => <Tag>{text || 'N/A'}</Tag>,
                    },
                    {
                      title: 'Status',
                      key: 'status',
                      render: () => <Tag color="orange">Pending</Tag>,
                    },
                  ]}
                  dataSource={employeesWithoutPayroll}
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50', '100'],
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} employees`,
                  }}
                  size="small"
                />
                <Divider />
                <RuleText style={{ textAlign: 'center', color: '#999', fontSize: 12 }}>
                  <strong>Total Employees:</strong> {payrollStats.totalEmployees} •
                  <strong style={{ marginLeft: 12 }}>Completed:</strong> {payrollStats.completedPayroll} •
                  <strong style={{ marginLeft: 12 }}>Remaining:</strong> {payrollStats.remainingPayroll}
                </RuleText>
              </>
            )}
          </Modal>

          {/* Salary View Modal */}
          <Modal
            title={
              <Space>
                <UserOutlined />
                Salary Summary
                {selectedMonth && ` - ${new Date(selectedMonth + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`}
                {selectedDepartment && ` - ${departments.find(d => d.id === parseInt(selectedDepartment))?.department_name || ''}`}
              </Space>
            }
            open={showSalaryModal}
            onCancel={() => setShowSalaryModal(false)}
            footer={null}
            width={1000}
            centered
          >
            {payrollData.length === 0 ? (
              <Empty
                description="No Salary Data"
                style={{ marginBottom: 0, marginTop: 40 }}
              >
                <p style={{ color: '#999', marginTop: 16 }}>
                  No payroll records found for the selected department and month.
                </p>
              </Empty>
            ) : (
              <>
                <Alert
                  message={`${payrollData.length} Employees Found`}
                  description={`Payroll data for ${new Date(selectedMonth + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`}
                  type="info"
                  style={{ marginBottom: 16 }}
                  icon={<UserOutlined />}
                />
                
                {/* Summary Cards */}
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                  <Col xs={12} sm={6}>
                    <Card bodyStyle={{ padding: '12px' }} style={{ borderLeft: '3px solid #722ed1' }}>
                      <Statistic
                        title="Total Employees"
                        value={payrollData.length}
                        valueStyle={{ color: '#722ed1', fontSize: 20 }}
                      />
                    </Card>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Card bodyStyle={{ padding: '12px' }} style={{ borderLeft: '3px solid #1890ff' }}>
                      <Statistic
                        title="Gross Salary"
                        value={payrollSummary.totalGrossSalary}
                        formatter={(value) => `฿${(value || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                        valueStyle={{ color: '#1890ff', fontSize: 18 }}
                      />
                    </Card>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Card bodyStyle={{ padding: '12px' }} style={{ borderLeft: '3px solid #ff4d4f' }}>
                      <Statistic
                        title="Total Deductions"
                        value={payrollSummary.totalDeductions}
                        formatter={(value) => `฿${(value || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                        valueStyle={{ color: '#ff4d4f', fontSize: 18 }}
                      />
                    </Card>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Card bodyStyle={{ padding: '12px' }} style={{ borderLeft: '3px solid #52c41a' }}>
                      <Statistic
                        title="Net Salary"
                        value={payrollSummary.totalNetSalary}
                        formatter={(value) => `฿${(value || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                        valueStyle={{ color: '#52c41a', fontSize: 18 }}
                      />
                    </Card>
                  </Col>
                </Row>

                {/* Payroll Details Table */}
                <Table
                  rowKey="id"
                  columns={[
                    {
                      title: 'Employee',
                      dataIndex: 'employeeName',
                      key: 'employeeName',
                      sorter: (a, b) => (a.employeeName || '').localeCompare(b.employeeName || ''),
                      render: (text) => <strong>{text || 'N/A'}</strong>,
                    },
                    {
                      title: 'Position',
                      dataIndex: 'position',
                      key: 'position',
                      sorter: (a, b) => (a.position || '').localeCompare(b.position || ''),
                      render: (text) => <Tag>{text || 'N/A'}</Tag>,
                    },
                    {
                      title: 'Monthly Salary',
                      dataIndex: 'monthlySalary',
                      key: 'monthlySalary',
                      sorter: (a, b) => (a.monthlySalary || 0) - (b.monthlySalary || 0),
                      render: (value) => <div style={{ textAlign: 'right' }}>฿{(value || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</div>,
                      align: 'right',
                    },
                    {
                      title: 'Gross Salary',
                      key: 'grossSalary',
                      sorter: (a, b) => (calculateGrossSalary(a) || 0) - (calculateGrossSalary(b) || 0),
                      render: (text, record) => <div style={{ textAlign: 'right' }}>฿{(calculateGrossSalary(record) || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</div>,
                      align: 'right',
                    },
                    {
                      title: 'Deductions',
                      key: 'deductions',
                      sorter: (a, b) => (calculateTotalDeductions(a) || 0) - (calculateTotalDeductions(b) || 0),
                      render: (text, record) => <div style={{ textAlign: 'right' }}>฿{(calculateTotalDeductions(record) || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</div>,
                      align: 'right',
                    },
                    {
                      title: 'Net Salary',
                      key: 'netSalary',
                      sorter: (a, b) => (calculateNetSalary(a) || 0) - (calculateNetSalary(b) || 0),
                      render: (text, record) => <div style={{ textAlign: 'right', color: '#52c41a', fontWeight: 'bold' }}>฿{(calculateNetSalary(record) || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</div>,
                      align: 'right',
                    }
                  ]}
                  dataSource={payrollData}
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50', '100'],
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} employees`,
                  }}
                  size="small"
                  scroll={{ x: 1000 }}
                />
              </>
            )}
          </Modal>

        </AntLayout>
      </ConfigProvider>

      <ToastContainer />
    </Layout>
  );
};

const RuleText = ({ children, style }) => <div style={style}>{children}</div>;

export default Payroll;
